import { useEffect, useMemo, useState } from 'react';
import T from '../constants/theme';
import { supabase } from '../lib/supabase';
import { fetchIsAdmin } from '../hooks/useIsAdmin';

// ── Constantes Fadr (synchro avec versions-api/lib/costTracker.js) ──
// Abo Fadr Plus = $10/mois ≈ 9,20 € à 0.92, inclut $10 d'API soit
// 200 minutes au tarif Create Stem Task ($0.05/min ≈ 0,046 €/min).
const FADR_EUR_PER_MIN = 0.046;
const FADR_PLAN_EUR = 9.20;
const FADR_PLAN_MINUTES = 200;

// ── Coûts infra récurrents (mensuel, en EUR) ──
// Ces lignes sont des constantes éditables — pas de tracking en DB. Mettre
// à jour à la main quand un plan change (upgrade, ré-évaluation, etc.).
// Les montants sont en EUR équivalents pour rester homogène avec le reste
// du dashboard. USD → EUR via taux ~0,92 (mis à jour 2026-04-29).
const INFRA_COSTS = [
  {
    label: 'Supabase Pro',
    eurMonth: 23.00,           // $25 × 0.92
    note: '100 Go storage + 250 Go egress + 5 Go max upload',
    tone: 'cerulean',
  },
  {
    label: 'Railway Hobby',
    eurMonth: 4.60,            // $5/mois × 0.92 — inclut $5 d'usage, suffit pour le volume actuel
    note: 'Backend versions-api · $5/mois flat (usage inclus)',
    tone: 'amber',
  },
  {
    label: 'Vercel',
    eurMonth: 0,
    note: 'Free tier — frontend autodeploy',
    tone: 'mint',
  },
];
const INFRA_TOTAL_EUR_MONTH = INFRA_COSTS.reduce((sum, c) => sum + c.eurMonth, 0);

/**
 * AdminScreen — dashboard admin (#/admin), gaté par public.admin_users.
 *
 * Sections :
 *   1. KPIs business (users, titres, recettes 30j, rentabilité 30j)
 *   2. KPIs coûts (moyenne, p95, total 30j, marge sur prix bas)
 *   3. Évolution coût quotidienne (bar chart SVG)
 *   4. Top consommateurs
 *   5. TOUS les utilisateurs (table cliquable, expand inline avec détail titres)
 *   6. Recettes (placeholder en attendant Stripe)
 *   7. Dernières analyses
 *
 * Note préfixe CSS : on n'utilise PAS `ad-*` (filtré par les adblockers
 * qui le confondent avec "advertisement"). Préfixe `cost-*` partout.
 *
 * Données récupérées via 3 RPC SECURITY DEFINER côté DB :
 *   - admin_get_global_stats()  → KPIs globaux + business
 *   - admin_get_user_stats()    → 1 ligne par user avec stats
 *   - admin_get_user_detail(id) → titres + versions d'un user (pour expand)
 *
 * Double barrière : gating UX côté front via la RPC is_admin(), et
 * gating sécurité réel côté Postgres (RLS is_admin() + check is_admin()
 * dans le corps des RPC admin_get_*).
 */
export default function AdminScreen() {
  // Statut admin lu depuis la base : RPC is_admin() → appartenance à
  // public.admin_users (migration 049). Plus de comparaison d'email
  // côté front, plus de VITE_ADMIN_EMAIL.
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [logs, setLogs] = useState([]);
  const [chatLogs, setChatLogs] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [userStats, setUserStats] = useState([]);
  const [revenue, setRevenue] = useState([]);
  // Retours testeurs — table public.feedback (migration 024). Comme
  // chat_cost_logs, on tolère l'erreur pour le cas où la migration n'a
  // pas encore été appliquée.
  const [feedbackRows, setFeedbackRows] = useState([]);
  // Installations plugin — RPC admin_get_plugin_installs (migration 045),
  // 1 ligne par user ayant ouvert le plugin (table plugin_first_seen).
  // null = RPC indisponible (migration pas appliquée), [] = zéro install.
  const [pluginInstalls, setPluginInstalls] = useState(null);
  // Parcours d'entrée — RPC admin_get_funnel (migration 046) : dates
  // fondatrices par user (première analyse / premier plugin / premier
  // paiement). null = RPC indisponible.
  const [funnelRows, setFunnelRows] = useState(null);
  // Provenance acquisition — migration 051 : visites/clics CTA anonymes
  // agrégés par jour × source (admin_get_landing_stats) + attribution
  // first-touch par compte (admin_get_user_attribution). null = RPC
  // indisponible (migration pas appliquée).
  const [landingStats, setLandingStats] = useState(null);
  const [userAttribution, setUserAttribution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Pour l'expand inline d'un user : null ou { userId, loading, items }
  const [expanded, setExpanded] = useState(null);
  // Pour l'expand inline d'un feedback : id de la ligne ouverte (string)
  // ou null si rien d'ouvert. Permet de lire les verbatims complets.
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  // Table utilisateurs : filtre texte (email/nom) + tri par colonne.
  // userSort = { key, dir } ou null (= tri par défaut de la RPC).
  const [userQuery, setUserQuery] = useState('');
  const [userSort, setUserSort] = useState(null);

  // ── Auth check + fetch toutes les données ───────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user: u } } = await supabase.auth.getUser();
        if (cancelled) return;
        const admin = u?.id ? await fetchIsAdmin(u.id) : false;
        if (cancelled) return;
        setIsAdmin(admin);
        setAuthChecked(true);
        if (!admin) {
          setLoading(false);
          return;
        }

        // Fetch en parallèle : analysis_cost_logs (30j), chat_cost_logs (30j),
        // 2 RPC, revenue_logs.
        const since = new Date();
        since.setDate(since.getDate() - 30);
        const [logsRes, chatLogsRes, globalRes, usersRes, revRes, feedbackRes, installsRes, funnelRes, landingRes, attribRes] = await Promise.all([
          supabase
            .from('analysis_cost_logs')
            .select('*')
            .gte('created_at', since.toISOString())
            .order('created_at', { ascending: false })
            .limit(2000),
          // chat_cost_logs : créée par migration 023, RLS admin-only.
          // Si la migration n'a pas encore été appliquée, on récupère un
          // tableau vide (PostgreSQL renvoie une erreur "relation does
          // not exist" qu'on attrape silencieusement pour ne pas planter
          // le dashboard tant que la migration n'est pas faite).
          supabase
            .from('chat_cost_logs')
            .select('*')
            .gte('created_at', since.toISOString())
            .order('created_at', { ascending: false })
            .limit(5000),
          supabase.rpc('admin_get_global_stats'),
          supabase.rpc('admin_get_user_stats'),
          supabase
            .from('revenue_logs')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(50),
          // feedback : table créée par migration 024, RLS admin-only sur
          // SELECT. Même tolérance que chat_cost_logs si la migration
          // n'a pas encore été appliquée. On charge tout l'historique
          // (limité à 1000) — volumes faibles tant qu'on est en beta.
          supabase
            .from('feedback')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(1000),
          // Installations plugin : RPC créée par migration 045. Même
          // tolérance que chat_cost_logs/feedback si pas encore appliquée.
          supabase.rpc('admin_get_plugin_installs'),
          // Parcours d'entrée : RPC créée par migration 046. Même tolérance.
          supabase.rpc('admin_get_funnel'),
          // Provenance (pub Meta & co) : RPC créées par migration 051.
          // Même tolérance tant que la migration n'est pas appliquée.
          supabase.rpc('admin_get_landing_stats', { p_days: 30 }),
          supabase.rpc('admin_get_user_attribution'),
        ]);

        if (cancelled) return;
        if (logsRes.error) throw logsRes.error;
        // On tolère l'erreur sur chat_cost_logs (table peut ne pas exister
        // avant que la migration 023 soit appliquée). On warn et on continue.
        if (chatLogsRes.error) {
          console.warn('[admin] chat_cost_logs unavailable (migration 023 not applied?):', chatLogsRes.error.message);
        }
        if (globalRes.error) throw globalRes.error;
        if (usersRes.error) throw usersRes.error;
        if (revRes.error) throw revRes.error;
        // Tolérance feedback (même rationale que chat_cost_logs).
        if (feedbackRes.error) {
          console.warn('[admin] feedback unavailable (migration 024 not applied?):', feedbackRes.error.message);
        }
        if (installsRes.error) {
          console.warn('[admin] admin_get_plugin_installs unavailable (migration 045 not applied?):', installsRes.error.message);
        }
        if (funnelRes.error) {
          console.warn('[admin] admin_get_funnel unavailable (migration 046 not applied?):', funnelRes.error.message);
        }
        if (landingRes.error) {
          console.warn('[admin] admin_get_landing_stats unavailable (migration 051 not applied?):', landingRes.error.message);
        }
        if (attribRes.error) {
          console.warn('[admin] admin_get_user_attribution unavailable (migration 051 not applied?):', attribRes.error.message);
        }

        setLogs(logsRes.data || []);
        setChatLogs(chatLogsRes.data || []);
        // RPC TABLE retourne un array, on prend la 1ère ligne pour global
        setGlobalStats(globalRes.data?.[0] || null);
        setUserStats(usersRes.data || []);
        setRevenue(revRes.data || []);
        setFeedbackRows(feedbackRes.data || []);
        setPluginInstalls(installsRes.error ? null : (installsRes.data || []));
        setFunnelRows(funnelRes.error ? null : (funnelRes.data || []));
        setLandingStats(landingRes.error ? null : (landingRes.data || []));
        setUserAttribution(attribRes.error ? null : (attribRes.data || []));
      } catch (e) {
        if (!cancelled) setErr(e.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Stats agrégées coûts (calculées côté JS depuis logs) ────
  const stats = useMemo(() => computeStats(logs), [logs]);
  const dailySeries = useMemo(() => computeDailySeries(logs, 30), [logs]);
  const topUsers = useMemo(() => computeTopUsers(logs, 10, userStats), [logs, userStats]);
  const fadrMonth = useMemo(() => computeFadrMonth(logs), [logs]);
  const stripeStats = useMemo(() => computeStripeStats(revenue, 30), [revenue]);
  const chatStats = useMemo(() => computeChatStats(chatLogs), [chatLogs]);
  // Feedback : on calcule deux fenêtres — "all" (depuis le début) et
  // "30d" (les 30 derniers jours). Les KPIs s'affichent pour 30j, le
  // total cumulé est rappelé en sub-label.
  const feedbackStats30d = useMemo(() => computeFeedbackStats(feedbackRows, 30), [feedbackRows]);
  const feedbackStatsAll = useMemo(() => computeFeedbackStats(feedbackRows, null), [feedbackRows]);
  // Acquisition : inscriptions (signed_up_at des user stats) + installations
  // plugin (first_seen_at) agrégées par jour sur 30 jours. Les
  // téléchargements (plugin_downloads) sont volontairement exclus de la
  // courbe — doublon avec l'installation réelle (décision 2026-07-10).
  // Équipe interne exclue des AGRÉGATS installs (compte + courbe), pour
  // s'aligner sur le cockpit Archipel. Les puces par-user (pluginByUid)
  // gardent la liste complète → l'install de l'équipe reste visible dans le
  // tableau, elle ne compte juste pas dans les totaux.
  const pluginInstallsExTeam = useMemo(
    () => (pluginInstalls || []).filter((p) => !TEAM_EMAILS.has((p.email || '').toLowerCase())),
    [pluginInstalls]
  );
  const acqSeries = useMemo(
    () => computeAcquisitionSeries(userStats, pluginInstallsExTeam, 30),
    [userStats, pluginInstallsExTeam]
  );
  const pluginTotals = useMemo(() => computePluginTotals(pluginInstallsExTeam), [pluginInstallsExTeam]);
  const funnel = useMemo(() => computeFunnel(funnelRows || []), [funnelRows]);
  // Provenance : agrégats par source (visites, CTA, inscrits) + funnel
  // Meta complet (croisé avec funnelRows pour "ont lancé une analyse").
  const sources = useMemo(
    () => computeSources(landingStats, userAttribution, funnelRows),
    [landingStats, userAttribution, funnelRows]
  );
  // Map user_id → install plugin (pour le badge Mac/Win dans la table users).
  const pluginByUid = useMemo(
    () => new Map((pluginInstalls || []).map((p) => [p.user_id, p])),
    [pluginInstalls]
  );

  // Utilisateurs affichés : filtre texte puis tri. Sans tri explicite, on
  // garde le comportement historique (balance croissante si business live,
  // coûts décroissants sinon).
  const visibleUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    let rows = userStats;
    if (q) {
      rows = rows.filter((u) =>
        (u.email || '').toLowerCase().includes(q) ||
        `${u.prenom || ''} ${u.nom || ''}`.toLowerCase().includes(q)
      );
    }
    const live = Number(globalStats?.total_revenue_all_time || 0) > 0;
    const base = live ? [...rows] : [...rows].sort((a, b) => Number(b.total_cost_eur || 0) - Number(a.total_cost_eur || 0));
    if (!userSort) return base;
    const { key, dir } = userSort;
    const val = (u) => {
      switch (key) {
        case 'email': return (u.email || '').toLowerCase();
        case 'name': return `${u.prenom || ''} ${u.nom || ''}`.trim().toLowerCase();
        case 'signed_up_at': return u.signed_up_at || '';
        case 'last_activity': return u.last_activity || '';
        case 'plugin': return pluginByUid.has(u.user_id) ? 1 : 0;
        default: return Number(u[key] || 0);
      }
    };
    return base.sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va === vb) return 0;
      // Vides / nulls toujours en bas, quel que soit le sens du tri.
      if (va === '' || va == null) return 1;
      if (vb === '' || vb == null) return -1;
      return (va < vb ? -1 : 1) * dir;
    });
  }, [userStats, userQuery, userSort, globalStats, pluginByUid]);

  // Clic sur un header : 1er clic = sens "naturel" (desc pour les nombres
  // et dates, asc pour les textes), 2e clic = sens inverse.
  function toggleUserSort(key) {
    const textCols = key === 'email' || key === 'name';
    setUserSort((prev) =>
      prev?.key === key ? { key, dir: -prev.dir } : { key, dir: textCols ? 1 : -1 }
    );
  }

  // Tant que Stripe n'est pas branché et qu'aucune vente n'est tombée
  // dans revenue_logs, on cache tout ce qui suppose une activité
  // commerciale (recettes, balance, marge). Réactivé automatiquement
  // dès qu'une recette > 0 apparaît dans globalStats.
  const isLiveBusiness = Number(globalStats?.total_revenue_all_time || 0) > 0;

  // ── Toggle expand sur une ligne user (charge le détail si besoin) ──
  async function toggleExpand(uid) {
    if (expanded?.userId === uid) { setExpanded(null); return; }
    setExpanded({ userId: uid, loading: true, items: [] });
    try {
      const { data, error } = await supabase.rpc('admin_get_user_detail', { target_user_id: uid });
      if (error) throw error;
      setExpanded({ userId: uid, loading: false, items: data || [] });
    } catch (e) {
      setExpanded({ userId: uid, loading: false, items: [], err: e.message });
    }
  }

  return (
    <div className="cost-screen">
      <AdminStyles />

      {/* Pas de topbar interne ici : la page admin tourne désormais
          en layout topbar (refonte 2026-04-30, cf. TOPBAR_SCREENS dans
          App.jsx). DashboardTopbar pose la nav (Accueil / Tarifs /
          Tableau de bord) en haut, DashboardRail pose les outils
          compte (crédits, Admin, Réglages, Déconnexion) en bas-gauche.
          Plus de sidebar. */}

      {!authChecked && <GateMsg label="Vérification…" />}
      {authChecked && !isAdmin && (
        <GateMsg
          label="Accès refusé."
          sub="Cette page est réservée aux administrateurs."
        />
      )}
      {authChecked && isAdmin && loading && <GateMsg label="Chargement des données…" />}
      {authChecked && isAdmin && err && <GateMsg label="Erreur" sub={err} />}

      {authChecked && isAdmin && !loading && !err && (
        <>
          {/* HERO discret */}
          <section className="cost-hero">
            <div className="cost-hero-inner">
              <div className="cost-eyebrow">
                Admin — {isLiveBusiness ? "Vue d'ensemble" : 'Phase de test'}
              </div>
              <h1 className="cost-hero-title">
                {isLiveBusiness ? (
                  <>Combien ça <em>coûte</em>, combien ça <em>rapporte</em>.</>
                ) : (
                  <>Combien ça <em>coûte</em>.</>
                )}
              </h1>
              <p className="cost-hero-sub">
                {globalStats
                  ? `${globalStats.total_users} user${globalStats.total_users > 1 ? 's' : ''} · ${globalStats.total_tracks} titre${globalStats.total_tracks > 1 ? 's' : ''} · ${globalStats.total_versions} version${globalStats.total_versions > 1 ? 's' : ''}`
                  : 'Données indisponibles'}
                {!isLiveBusiness && ' · monétisation pas encore active'}
              </p>
            </div>
          </section>

          {/* SECTION KPIs BUSINESS */}
          <section className="cost-section">
            <div className="cost-section-eyebrow">
              {isLiveBusiness ? 'Business — 30 derniers jours' : 'Activité — 30 derniers jours'}
            </div>
            <h2 className="cost-section-title">
              {isLiveBusiness ? (
                <>La <em>rentabilité</em> en un coup d'œil.</>
              ) : (
                <>L'<em>activité</em> en un coup d'œil.</>
              )}
            </h2>
            <div className="cost-kpi-grid">
              {isLiveBusiness && (
                <KpiCard
                  label="Recettes nettes 30j"
                  value={fmtEur(stripeStats.net_30d)}
                  sub={`Brut TTC : ${fmtEur(stripeStats.brut_30d)} · frais Stripe : ${fmtEur(stripeStats.fees_30d)}`}
                  tone="mint"
                />
              )}
              <KpiCard
                label="Coûts API 30j"
                value={fmtEur(globalStats?.total_cost_30d || 0)}
                sub={`Total all-time : ${fmtEur(globalStats?.total_cost_all_time || 0)}`}
                tone="amber"
              />
              {isLiveBusiness && (
                <KpiCard
                  label="Balance nette 30j"
                  value={fmtEur(stripeStats.net_30d - (globalStats?.total_cost_30d || 0))}
                  sub={(stripeStats.net_30d - (globalStats?.total_cost_30d || 0)) >= 0 ? 'Rentable ✓ (frais déduits)' : 'Déficit en cours'}
                  tone={(stripeStats.net_30d - (globalStats?.total_cost_30d || 0)) >= 0 ? 'mint' : 'red'}
                />
              )}
              {!isLiveBusiness && (
                <KpiCard
                  label="Analyses 30j"
                  value={String(stats.count)}
                  sub={`Total all-time : ${fmtEur(globalStats?.total_cost_all_time || 0)} de coûts`}
                  tone="violet"
                />
              )}
              <KpiCard
                label="Nouveaux users 30j"
                value={String(globalStats?.new_signups_30d || 0)}
                sub={`Total inscrits : ${globalStats?.total_users || 0}`}
                tone="cerulean"
              />
              {!isLiveBusiness && (
                <KpiCard
                  label="Coût moyen / user"
                  value={fmtEur(globalStats?.total_users > 0 ? (globalStats.total_cost_all_time || 0) / globalStats.total_users : 0)}
                  sub={`sur ${globalStats?.total_users || 0} comptes`}
                  tone="amber"
                />
              )}
            </div>
          </section>

          {/* SECTION ACQUISITION — inscriptions + installations plugin.
              Courbe alimentée par admin_get_user_stats (signed_up_at) et
              admin_get_plugin_installs (migration 045, table
              plugin_first_seen = installations réelles). Les
              téléchargements (plugin_downloads) sont volontairement
              absents de la courbe et des KPIs : doublon avec
              l'installation, faux positifs mobiles. */}
          <section className="cost-section">
            <div className="cost-section-eyebrow">Acquisition — 30 derniers jours</div>
            <h2 className="cost-section-title">
              Inscriptions et <em>installations plugin</em> par jour.
            </h2>
            <div className="cost-kpi-grid">
              <KpiCard
                label="Inscriptions 30j"
                value={String(acqSeries.reduce((s, d) => s + d.signups, 0))}
                sub={`Total inscrits : ${globalStats?.total_users || 0}`}
                tone="cerulean"
              />
              <KpiCard
                label="Installations plugin 30j"
                value={String(acqSeries.reduce((s, d) => s + d.installs, 0))}
                sub={`Total : ${pluginTotals.total} machine${pluginTotals.total > 1 ? 's' : ''} depuis le lancement`}
                tone="mint"
              />
              <KpiCard
                label="Utilisateurs plugin"
                value={String(pluginTotals.total)}
                sub={`${pluginTotals.mac} Mac · ${pluginTotals.windows} Windows${pluginTotals.unknown > 0 ? ` · ${pluginTotals.unknown} inconnu${pluginTotals.unknown > 1 ? 's' : ''}` : ''}`}
                tone="amber"
              />
              <KpiCard
                label="Taux d'équipement"
                value={globalStats?.total_users > 0 ? `${Math.round((pluginTotals.total / globalStats.total_users) * 100)} %` : '—'}
                sub="des inscrits ont ouvert le plugin au moins une fois"
                tone="violet"
              />
            </div>
            <AcquisitionChart series={acqSeries} />
            {pluginInstalls === null && (
              <div className="cost-fadr-note">
                La RPC <code>admin_get_plugin_installs</code> est indisponible — la migration 045
                n'est probablement pas encore appliquée. La courbe n'affiche que les inscriptions.
              </div>
            )}
          </section>

          {/* SECTION PROVENANCE — qui vient de la pub Meta (migration 051).
              Visites + clics CTA = sessions navigateur ANONYMES
              (landing_events, loggées par la RPC log_landing_event dès
              l'arrivée sur le site). Inscriptions = comptes rattachés à
              leur provenance first-touch au premier SIGNED_IN
              (user_attribution). Détection Meta même sans UTM dans
              l'URL de l'ad : le fbclid auto-ajouté par FB/IG suffit. */}
          {(landingStats !== null || userAttribution !== null) && (
            <section className="cost-section">
              <div className="cost-section-eyebrow">Provenance — pub Meta &amp; sources · 30 derniers jours</div>
              <h2 className="cost-section-title">
                Qui vient de la <em>pub Meta</em>, et jusqu'où ils vont.
              </h2>
              <div className="cost-kpi-grid">
                <KpiCard
                  label="Visites via Meta"
                  value={String(sources.meta.visits)}
                  sub={`sur ${sources.totalVisits} visite${sources.totalVisits > 1 ? 's' : ''} trackée${sources.totalVisits > 1 ? 's' : ''} toutes sources`}
                  tone="cerulean"
                />
                <KpiCard
                  label="Ont passé le CTA"
                  value={String(sources.meta.cta)}
                  sub={`${pctOf(sources.meta.cta, sources.meta.visits)} des visites Meta cliquent un CTA d'inscription`}
                  tone="mint"
                />
                <KpiCard
                  label="Inscriptions via Meta"
                  value={String(sources.meta.signups.length)}
                  sub={`${pctOf(sources.meta.signups.length, sources.meta.visits)} des visites Meta créent un compte`}
                  tone="violet"
                />
                <KpiCard
                  label="Réactivations Meta"
                  value={String(sources.meta.returning.length)}
                  sub="comptes existants revenus via la pub (pas des inscriptions)"
                  tone="amber"
                />
              </div>
              <div className="cost-funnel-grid">
                <FunnelBlock
                  title="Funnel pub Meta"
                  color="#7a9ee0"
                  steps={[
                    { label: 'Arrivés sur le site', count: sources.meta.visits },
                    { label: 'Ont passé le CTA', count: sources.meta.cta },
                    { label: 'Ont créé un compte', count: sources.meta.signups.length },
                    { label: 'Ont lancé une analyse', count: sources.meta.analyzed },
                  ]}
                />
                <div className="cost-table-wrap">
                  <table className="cost-table">
                    <thead>
                      <tr>
                        <th>Source</th>
                        <th>Visites</th>
                        <th>CTA</th>
                        <th>Inscrits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sources.rows.map((r) => (
                        <tr key={r.source}>
                          <td>{r.source}</td>
                          <td>{r.visits}</td>
                          <td>{r.cta}</td>
                          <td>{r.signups}</td>
                        </tr>
                      ))}
                      {sources.rows.length === 0 && (
                        <tr><td colSpan={4}>Aucune visite trackée pour l'instant.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {sources.meta.signups.length > 0 && (
                <div className="cost-fadr-note">
                  <strong>Inscrits via Meta :</strong>{' '}
                  {sources.meta.signups
                    .map((u) => `${u.email}${u.utm_campaign ? ` (${u.utm_campaign})` : ''}`)
                    .join(' · ')}
                </div>
              )}
              <div className="cost-fadr-note">
                Visites et CTA = sessions navigateur anonymes (table <code>landing_events</code>,
                une visite et un clic CTA max par session). Inscriptions = provenance first-touch
                rattachée au compte au premier login (<code>user_attribution</code>). Un compte
                créé AVANT sa première arrivée taguée Meta est compté en « réactivation », pas en
                inscription. Source « meta » = UTM meta/fb/ig OU présence du <code>fbclid</code> ;
                « meta-organique » = referrer facebook/instagram sans clic pub tagué.
              </div>
            </section>
          )}

          {/* SECTION PARCOURS D'ENTRÉE — le funnel n'est pas linéaire :
              certains entrent par l'analyse web, d'autres par le plugin
              (décision David 2026-07-10). Segmentation par PREMIÈRE action
              (RPC admin_get_funnel, migration 046) + un mini-funnel par
              porte d'entrée + KPI de croisement. */}
          {funnelRows !== null && (
            <section className="cost-section">
              <div className="cost-section-eyebrow">Parcours d'entrée</div>
              <h2 className="cost-section-title">
                Par où ils <em>entrent</em>, jusqu'où ils <em>vont</em>.
              </h2>
              <div className="cost-kpi-grid">
                <KpiCard
                  label="Entrés par l'analyse"
                  value={String(funnel.site.length)}
                  sub={`${pctOf(funnel.site.length, funnel.total)} des inscrits — première action : analyse web`}
                  tone="cerulean"
                />
                <KpiCard
                  label="Entrés par le plugin"
                  value={String(funnel.plugin.length)}
                  sub={`${pctOf(funnel.plugin.length, funnel.total)} des inscrits — première action : plugin DAW`}
                  tone="mint"
                />
                <KpiCard
                  label="Utilisent les deux"
                  value={String(funnel.both)}
                  sub="site + plugin — l'ancrage réel dans l'écosystème"
                  tone="violet"
                />
                <KpiCard
                  label="Dormants"
                  value={String(funnel.dormant)}
                  sub={`${pctOf(funnel.dormant, funnel.total)} des inscrits — aucune action après le signup`}
                  tone="amber"
                />
              </div>
              <div className="cost-funnel-grid">
                <FunnelBlock
                  title="Entrée par l'analyse web"
                  color="#5cb8cc"
                  steps={[
                    { label: 'Ont analysé un titre', count: funnel.site.length },
                    { label: 'Ont ensuite installé le plugin', count: funnel.siteToPlugin },
                    { label: 'Ont payé', count: funnel.sitePaid },
                  ]}
                />
                <FunnelBlock
                  title="Entrée par le plugin"
                  color="#8ee07a"
                  steps={[
                    { label: 'Ont installé le plugin', count: funnel.plugin.length },
                    { label: 'Ont ensuite analysé un titre', count: funnel.pluginToSite },
                    { label: 'Ont payé', count: funnel.pluginPaid },
                  ]}
                />
              </div>
              <div className="cost-fadr-note">
                Porte d'entrée = première action après l'inscription (première version uploadée
                vs première ouverture du plugin). Paiement = premier encaissement réel
                dans <code>revenue_logs</code>. Les dormants n'apparaissent dans aucun des deux funnels.
              </div>
            </section>
          )}

          {/* SECTION KPIs COÛTS */}
          <section className="cost-section">
            <div className="cost-section-eyebrow">Coûts par analyse</div>
            <h2 className="cost-section-title">
              Le <em>vrai prix</em> d'une analyse.
            </h2>
            <div className="cost-kpi-grid">
              <KpiCard
                label="Coût moyen"
                value={fmtEur(stats.avg)}
                sub={`médiane ${fmtEur(stats.median)}`}
                tone="amber"
              />
              <KpiCard
                label="P95 (worst case)"
                value={fmtEur(stats.p95)}
                sub="95 % des analyses coûtent moins"
                tone="violet"
              />
              <KpiCard
                label="Total 30 jours"
                value={fmtEur(stats.total)}
                sub={`${stats.count} analyses`}
                tone="cerulean"
              />
              {isLiveBusiness ? (
                <KpiCard
                  label="Marge sur prix bas (3,00 €)"
                  value={pctMarge(stats.avg, 3.0)}
                  sub="vs prix unit. plancher de 3 €"
                  tone={stats.avg < 1.0 ? 'mint' : 'red'}
                />
              ) : (
                <KpiCard
                  label="Min observé"
                  value={fmtEur(stats.min)}
                  sub={`max ${fmtEur(stats.max)}`}
                  tone="mint"
                />
              )}
            </div>
          </section>

          {/* SECTION COÛTS INFRA RÉCURRENTS — fixed, mensuel, hors API */}
          <section className="cost-section">
            <div className="cost-section-eyebrow">Coûts infra récurrents</div>
            <h2 className="cost-section-title">
              Le <em>fixe mensuel</em> hors coûts d'analyse.
            </h2>
            <div className="cost-kpi-grid">
              {INFRA_COSTS.map((c) => (
                <KpiCard
                  key={c.label}
                  label={c.label}
                  value={fmtEur(c.eurMonth)}
                  sub={c.note}
                  tone={c.tone}
                />
              ))}
              <KpiCard
                label="Total infra / mois"
                value={fmtEur(INFRA_TOTAL_EUR_MONTH)}
                sub={`soit ${fmtEur(INFRA_TOTAL_EUR_MONTH * 12)} / an`}
                tone="violet"
              />
            </div>
            {/* Petit récap ligne pour mettre en perspective avec coûts d'analyse */}
            <div className="cost-fadr-note" style={{ marginTop: 14 }}>
              <strong>Coût total mensuel estimé</strong> = {fmtEur(INFRA_TOTAL_EUR_MONTH)} fixe + {fmtEur(stats.total)} d'API sur les 30 derniers jours
              {' = '}
              <strong>{fmtEur(INFRA_TOTAL_EUR_MONTH + stats.total)}</strong>.
              {isLiveBusiness && (
                <>
                  {' '}Recettes nettes 30j&nbsp;: <strong>{fmtEur(stripeStats.net_30d)}</strong> →
                  {' '}<strong style={{ color: stripeStats.net_30d - INFRA_TOTAL_EUR_MONTH - stats.total >= 0 ? '#8ee07a' : '#ef6b6b' }}>
                    Balance globale&nbsp;: {fmtEur(stripeStats.net_30d - INFRA_TOTAL_EUR_MONTH - stats.total)}
                  </strong>.
                </>
              )}
            </div>
          </section>

          {/* SECTION ÉVOLUTION */}
          <section className="cost-section">
            <div className="cost-section-eyebrow">Évolution coût sur 30 jours</div>
            <h2 className="cost-section-title">
              Volume et coût total <em>par jour</em>.
            </h2>
            <DailyChart series={dailySeries} />
          </section>

          {/* SECTION ABONNEMENT FADR */}
          <section className="cost-section">
            <div className="cost-section-eyebrow">Abonnement Fadr Plus — mois en cours</div>
            <h2 className="cost-section-title">
              Combien de <em>crédits</em> il me reste sur les 9,20 € inclus.
            </h2>
            <div className="cost-kpi-grid">
              <KpiCard
                label="Minutes consommées"
                value={`${fadrMonth.minutesUsed.toFixed(0)} / ${FADR_PLAN_MINUTES}`}
                sub={`${Math.round(fadrMonth.pctUsed * 100)} % de la franchise`}
                tone={fadrMonth.pctUsed >= 0.8 ? 'red' : fadrMonth.pctUsed >= 0.5 ? 'amber' : 'mint'}
              />
              <KpiCard
                label="Crédits utilisés"
                value={fmtEur(fadrMonth.eurUsed)}
                sub={`sur ${fmtEur(FADR_PLAN_EUR)} inclus dans l'abo`}
                tone={fadrMonth.pctUsed >= 0.8 ? 'red' : fadrMonth.pctUsed >= 0.5 ? 'amber' : 'cerulean'}
              />
              <KpiCard
                label="Analyses Fadr ce mois"
                value={String(fadrMonth.count)}
                sub={fadrMonth.avgDurationMin > 0 ? `durée moy. ${fadrMonth.avgDurationMin.toFixed(1)} min` : 'aucune analyse loggée'}
                tone="violet"
              />
              <KpiCard
                label="Reste estimé"
                value={fadrMonth.analysesLeft != null ? `~${fadrMonth.analysesLeft} analyses` : '—'}
                sub={fadrMonth.analysesLeft != null
                  ? 'avant facturation au-delà'
                  : 'logger des analyses pour estimer'}
                tone={fadrMonth.analysesLeft != null && fadrMonth.analysesLeft <= 5 ? 'red' : 'mint'}
              />
            </div>
            <div className="cost-fadr-note">
              Tarif Fadr : {fmtEur(FADR_EUR_PER_MIN)}/minute d'audio (≈ $0,05).
              L'abonnement Fadr Plus ($10/mois) inclut {FADR_PLAN_MINUTES} minutes.
              Au-delà, chaque minute supplémentaire est facturée au même tarif.
              {fadrMonth.eurOver > 0 && (
                <> <strong>Dépassement ce mois : +{fmtEur(fadrMonth.eurOver)}.</strong></>
              )}
            </div>
          </section>

          {/* SECTION COÛTS DU CHAT — alimentée par chat_cost_logs (mig 023). */}
          <section className="cost-section">
            <div className="cost-section-eyebrow">Chat — 30 derniers jours</div>
            <h2 className="cost-section-title">
              Combien me coûte le <em>chat de fiche</em>.
            </h2>
            <div className="cost-kpi-grid">
              <KpiCard
                label="Coût total chat 30j"
                value={fmtEur(chatStats.total)}
                sub={`${chatStats.count} tour${chatStats.count > 1 ? 's' : ''} loggé${chatStats.count > 1 ? 's' : ''}`}
                tone="amber"
              />
              <KpiCard
                label="Coût moyen / tour"
                value={fmtEur(chatStats.avg)}
                sub={chatStats.count > 0
                  ? `~${Math.round((chatStats.total_in + chatStats.total_cache_w + chatStats.total_cache_r) / chatStats.count)} input tokens / tour`
                  : 'aucun tour loggé'}
                tone="cerulean"
              />
              <KpiCard
                label="Cache hit rate"
                value={`${Math.round(chatStats.cache_hit_rate * 100)} %`}
                sub={`${chatStats.total_cache_r.toLocaleString('fr-FR')} tokens lus / ${chatStats.total_cache_w.toLocaleString('fr-FR')} écrits`}
                tone={chatStats.cache_hit_rate >= 0.6 ? 'mint' : chatStats.cache_hit_rate >= 0.3 ? 'amber' : 'red'}
              />
              <KpiCard
                label="Économies prompt caching"
                value={chatStats.savings_eur > 0 ? `+ ${fmtEur(chatStats.savings_eur)}` : fmtEur(chatStats.savings_eur)}
                sub={chatStats.savings_eur > 0
                  ? 'sauvés vs sans caching'
                  : 'cache pas encore amorti'}
                tone={chatStats.savings_eur > 0 ? 'mint' : 'violet'}
              />
            </div>
            <div className="cost-fadr-note">
              Tarification Sonnet 4.6 : input plein {fmtEur(2.76 / 1000)}/k tokens · cache write ×1.25 · cache read ×0.10 · output {fmtEur(13.80 / 1000)}/k tokens.
              {chatStats.count > 0 && (
                <> RAG PureMix mobilisé sur <strong>{Math.round(chatStats.rag_rate * 100)} %</strong> des tours ({chatStats.rag_hits}/{chatStats.count}).</>
              )}
              {chatStats.count === 0 && (
                <> Aucun tour de chat sur les 30 derniers jours — la table est peut-être encore vide ou la migration 023 pas encore appliquée.</>
              )}
            </div>
          </section>

          {/* SECTION FEEDBACK TESTEURS — alimentée par public.feedback (mig 024).
              Présentée juste après les coûts pour qu'elle soit visible "à hauteur
              d'œil" : c'est l'autre indicateur clé en phase beta (l'argent et
              les retours utilisateurs). */}
          <section className="cost-section">
            <div className="cost-section-eyebrow">Retours testeurs — 30 derniers jours</div>
            <h2 className="cost-section-title">
              Ce que les testeurs <em>pensent</em> de Versions.
            </h2>
            <div className="cost-kpi-grid">
              <KpiCard
                label="Score NPS (30j)"
                value={feedbackStats30d.npsCount > 0 ? (feedbackStats30d.npsScore > 0 ? '+' + feedbackStats30d.npsScore : String(feedbackStats30d.npsScore)) : '—'}
                sub={feedbackStats30d.npsCount > 0
                  ? `${feedbackStats30d.promoters} promoteur${feedbackStats30d.promoters > 1 ? 's' : ''} · ${feedbackStats30d.passives} passif${feedbackStats30d.passives > 1 ? 's' : ''} · ${feedbackStats30d.detractors} détracteur${feedbackStats30d.detractors > 1 ? 's' : ''}`
                  : 'aucune note NPS reçue'}
                tone={feedbackStats30d.npsScore >= 30 ? 'mint' : feedbackStats30d.npsScore >= 0 ? 'amber' : 'red'}
              />
              <KpiCard
                label="Retours reçus 30j"
                value={feedbackStats30d.count.toString()}
                sub={feedbackStatsAll.count > feedbackStats30d.count
                  ? `${feedbackStatsAll.count} au total depuis le lancement`
                  : 'depuis le lancement'}
                tone="cerulean"
              />
              <KpiCard
                label="NPS moyen brut"
                value={feedbackStats30d.npsCount > 0 ? feedbackStats30d.npsAvg.toFixed(1) + ' / 10' : '—'}
                sub={feedbackStats30d.npsCount > 0
                  ? `sur ${feedbackStats30d.npsCount} note${feedbackStats30d.npsCount > 1 ? 's' : ''}`
                  : 'aucune note'}
                tone={feedbackStats30d.npsAvg >= 8 ? 'mint' : feedbackStats30d.npsAvg >= 6 ? 'amber' : 'red'}
              />
              <KpiCard
                label="Taux de verbatims"
                value={feedbackStats30d.count > 0 ? Math.round(feedbackStats30d.verbatimRate * 100) + ' %' : '—'}
                sub={feedbackStats30d.count > 0
                  ? 'des retours contiennent un texte libre'
                  : 'pas encore de retour'}
                tone={feedbackStats30d.verbatimRate >= 0.7 ? 'mint' : feedbackStats30d.verbatimRate >= 0.4 ? 'amber' : 'violet'}
              />
            </div>
            <div className="cost-fadr-note">
              {feedbackStatsAll.count === 0 && (
                <>Aucun retour pour l'instant — la table est peut-être encore vide ou la migration 024 pas encore appliquée.</>
              )}
              {feedbackStatsAll.count > 0 && (
                <>
                  NPS lecture rapide : <strong style={{color: T.mint}}>{'>'} +30</strong> = excellent · <strong style={{color: T.amber}}>0 à +30</strong> = correct · <strong style={{color: T.red}}>{'<'} 0</strong> = signal d'alarme.
                  Promoteur = note 9-10, Passif = 7-8, Détracteur = 0-6.
                </>
              )}
            </div>

            {feedbackStatsAll.count > 0 && (
              <FeedbackTable
                rows={feedbackRows}
                userStats={userStats}
                expandedId={expandedFeedback}
                onToggleExpand={(id) => setExpandedFeedback(expandedFeedback === id ? null : id)}
              />
            )}
          </section>

          {/* SECTION TOUS LES USERS */}
          <section className="cost-section">
            <div className="cost-section-eyebrow">Tous les utilisateurs</div>
            <h2 className="cost-section-title">
              {userStats.length} compte{userStats.length > 1 ? 's' : ''} · classés par{' '}
              {isLiveBusiness ? <em>balance croissante</em> : <em>coûts décroissants</em>}
            </h2>
            <div className="cost-table-tools">
              <input
                className="cost-search"
                type="search"
                placeholder="Filtrer par email ou nom…"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                aria-label="Filtrer les utilisateurs"
              />
              {(userQuery || userSort) && (
                <button
                  className="cost-search-reset"
                  onClick={() => { setUserQuery(''); setUserSort(null); }}
                >
                  Réinitialiser
                </button>
              )}
              <span className="cost-search-count">
                {visibleUsers.length === userStats.length
                  ? `${userStats.length} compte${userStats.length > 1 ? 's' : ''}`
                  : `${visibleUsers.length} / ${userStats.length}`}
              </span>
            </div>
            <div className="cost-table-wrap">
              <table className="cost-table">
                <thead>
                  <tr>
                    <SortTh label="Email" k="email" sort={userSort} onSort={toggleUserSort} />
                    <SortTh label="Nom" k="name" sort={userSort} onSort={toggleUserSort} />
                    <SortTh label="Plugin" k="plugin" sort={userSort} onSort={toggleUserSort} align="center" />
                    <SortTh label="Inscrit" k="signed_up_at" sort={userSort} onSort={toggleUserSort} align="center" />
                    <SortTh label="Dernière activité" k="last_activity" sort={userSort} onSort={toggleUserSort} align="center" />
                    <SortTh label="Projets" k="projects_count" sort={userSort} onSort={toggleUserSort} align="right" />
                    <SortTh label="Titres" k="tracks_count" sort={userSort} onSort={toggleUserSort} align="right" />
                    <SortTh label="Versions" k="versions_count" sort={userSort} onSort={toggleUserSort} align="right" />
                    <SortTh label="Coûts" k="total_cost_eur" sort={userSort} onSort={toggleUserSort} align="right" />
                    {isLiveBusiness && <SortTh label="Recettes" k="total_revenue_eur" sort={userSort} onSort={toggleUserSort} align="right" />}
                    {isLiveBusiness && <SortTh label="Balance" k="balance_eur" sort={userSort} onSort={toggleUserSort} align="right" />}
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.length === 0 && (
                    <tr><td colSpan={isLiveBusiness ? 11 : 9} className="cost-empty">
                      {userQuery ? 'Aucun utilisateur ne correspond au filtre.' : 'Aucun utilisateur.'}
                    </td></tr>
                  )}
                  {visibleUsers.map((u) => {
                    const isOpen = expanded?.userId === u.user_id;
                    const balance = Number(u.balance_eur || 0);
                    return (
                      <>
                        <tr
                          key={u.user_id}
                          className={`cost-row-clickable ${isOpen ? 'cost-row-open' : ''}`}
                          onClick={() => toggleExpand(u.user_id)}
                          aria-expanded={isOpen}
                        >
                          <td className="cost-email">{u.email || '—'}</td>
                          <td className="cost-muted">{[u.prenom, u.nom].filter(Boolean).join(' ') || <span className="cost-anon">—</span>}</td>
                          <td style={{textAlign: 'center'}}>
                            <PluginChip install={pluginByUid.get(u.user_id)} />
                          </td>
                          <td className="cost-muted" style={{textAlign: 'center'}}>{fmtDate(u.signed_up_at, true)}</td>
                          <td className="cost-muted" style={{textAlign: 'center'}}>{u.last_activity ? fmtDate(u.last_activity, true) : <span className="cost-anon">jamais</span>}</td>
                          <td style={{textAlign: 'right'}}>{u.projects_count}</td>
                          <td style={{textAlign: 'right'}}>{u.tracks_count}</td>
                          <td style={{textAlign: 'right'}}>{u.versions_count}</td>
                          <td style={{textAlign: 'right'}} className="cost-muted">{fmtEur(u.total_cost_eur)}</td>
                          {isLiveBusiness && (
                            <td style={{textAlign: 'right'}} className="cost-muted">{fmtEur(u.total_revenue_eur)}</td>
                          )}
                          {isLiveBusiness && (
                            <td style={{textAlign: 'right'}} className={balance >= 0 ? 'cost-balance-pos' : 'cost-balance-neg'}>
                              {fmtEur(balance)}
                            </td>
                          )}
                        </tr>
                        {isOpen && (
                          <tr className="cost-row-detail">
                            <td colSpan={isLiveBusiness ? 11 : 9}>
                              <UserDetail data={expanded} />
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* SECTION TOP CONSOMMATEURS (par coût sur 30j, basé sur logs) */}
          <section className="cost-section">
            <div className="cost-section-eyebrow">Top consommateurs sur 30 jours</div>
            <h2 className="cost-section-title">
              Qui coûte <em>le plus</em> en API.
            </h2>
            <div className="cost-table-wrap">
              <table className="cost-table">
                <thead>
                  <tr>
                    <th style={{width: '50px'}}>#</th>
                    <th>Email</th>
                    <th style={{textAlign: 'right'}}>Analyses</th>
                    <th style={{textAlign: 'right'}}>Coût total</th>
                    <th style={{textAlign: 'right'}}>Coût moyen / analyse</th>
                  </tr>
                </thead>
                <tbody>
                  {topUsers.length === 0 && (
                    <tr><td colSpan="5" className="cost-empty">Aucune donnée sur la période.</td></tr>
                  )}
                  {topUsers.map((u, i) => (
                    <tr key={u.userId || i}>
                      <td className="cost-rank">{i + 1}</td>
                      <td className="cost-email">{u.email || (u.userId ? u.userId.slice(0, 8) + '…' : <span className="cost-anon">anonyme</span>)}</td>
                      <td style={{textAlign: 'right'}}>{u.count}</td>
                      <td style={{textAlign: 'right'}} className="cost-total">{fmtEur(u.total)}</td>
                      <td style={{textAlign: 'right'}} className="cost-muted">{fmtEur(u.total / u.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* SECTION RECETTES — visible uniquement quand au moins une vente
              est tombée. Avant Stripe : section masquée pour ne pas
              polluer l'admin avec des KPIs vides. */}
          {isLiveBusiness && (
            <section className="cost-section">
              <div className="cost-section-eyebrow">Historique des recettes</div>
              <h2 className="cost-section-title">
                Tout ce qui <em>rentre</em>.
              </h2>
              {revenue.length === 0 ? (
                <div className="cost-empty-card">
                  Aucune recette dans la fenêtre récente.
                </div>
              ) : (
                <div className="cost-table-wrap">
                  <table className="cost-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>User</th>
                        <th>Source</th>
                        <th>Produit</th>
                        <th style={{textAlign: 'right'}}>Brut TTC</th>
                        <th style={{textAlign: 'right'}}>Net après Stripe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenue.map((r) => (
                        <tr key={r.id}>
                          <td className="cost-muted">{fmtDate(r.created_at)}</td>
                          <td className="cost-userid">{r.user_id ? r.user_id.slice(0, 8) + '…' : <span className="cost-anon">—</span>}</td>
                          <td className="cost-muted">{r.source}</td>
                          <td className="cost-muted">{r.product || '—'}</td>
                          <td style={{textAlign: 'right'}} className="cost-total">{fmtEur(r.amount_eur)}</td>
                          <td style={{textAlign: 'right'}} className="cost-muted">{r.net_eur != null ? fmtEur(r.net_eur) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* SECTION DERNIÈRES ANALYSES */}
          <section className="cost-section">
            <div className="cost-section-eyebrow">Dernières analyses</div>
            <h2 className="cost-section-title">
              Les 20 plus <em>récentes</em>.
            </h2>
            <div className="cost-table-wrap">
              <table className="cost-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>User</th>
                    <th style={{textAlign: 'right'}}>Durée audio</th>
                    <th style={{textAlign: 'right'}}>Gemini</th>
                    <th style={{textAlign: 'right'}}>Claude</th>
                    <th style={{textAlign: 'right'}}>Fadr</th>
                    <th style={{textAlign: 'right'}}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 && (
                    <tr><td colSpan="7" className="cost-empty">Aucune analyse loggée.</td></tr>
                  )}
                  {logs.slice(0, 20).map((l) => (
                    <tr key={l.id}>
                      <td className="cost-muted">{fmtDate(l.created_at)}</td>
                      <td className="cost-userid">{l.user_id ? l.user_id.slice(0, 8) + '…' : <span className="cost-anon">—</span>}</td>
                      <td style={{textAlign: 'right'}} className="cost-muted">{fmtDuration(l.audio_duration_sec)}</td>
                      <td style={{textAlign: 'right'}} className="cost-muted">{fmtEur(l.gemini_eur)}</td>
                      <td style={{textAlign: 'right'}} className="cost-muted">{fmtEur(l.claude_eur)}</td>
                      <td style={{textAlign: 'right'}} className="cost-muted">{fmtEur(l.fadr_eur)}</td>
                      <td style={{textAlign: 'right'}} className="cost-total">{fmtEur(l.total_eur)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <footer className="cost-footer">
            <div className="cost-footer-mark">
              VER<span className="accent">Si</span>ONS
            </div>
            <div className="cost-footer-line">
              Tarifs unitaires définis dans <code>versions-api/lib/costTracker.js</code>.
              Recettes alimentées via <code>revenue_logs</code> (Stripe webhook à brancher).
            </div>
          </footer>
        </>
      )}
    </div>
  );
}

/* ── Sub-components ───────────────────────────────────── */
function GateMsg({ label, sub }) {
  return (
    <div className="cost-gate">
      <div className="cost-gate-label">{label}</div>
      {sub && <div className="cost-gate-sub">{sub}</div>}
    </div>
  );
}

/**
 * SortTh — header de colonne triable. Flèche ▲/▼ quand la colonne est
 * active, clic pour trier / inverser. Le sens initial est décidé par
 * toggleUserSort (desc pour nombres/dates, asc pour textes).
 */
function SortTh({ label, k, sort, onSort, align }) {
  const active = sort?.key === k;
  return (
    <th
      style={{ textAlign: align || 'left' }}
      className={`cost-th-sort ${active ? 'cost-th-sort-active' : ''}`}
      onClick={() => onSort(k)}
      title="Cliquer pour trier"
      aria-sort={active ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      <span className="cost-th-arrow">{active ? (sort.dir === 1 ? '▲' : '▼') : ''}</span>
    </th>
  );
}

/**
 * PluginChip — pastille "plugin installé" dans la table users.
 * Icône prise SVG (pas d'emoji) + label MAC / WIN / OUI (plateforme
 * inconnue). Tooltip natif avec la date de première ouverture.
 */
function PluginChip({ install }) {
  if (!install) return <span className="cost-anon">—</span>;
  const label = install.platform === 'mac' ? 'MAC' : install.platform === 'windows' ? 'WIN' : 'OUI';
  const when = install.first_seen_at ? fmtDate(install.first_seen_at, true) : null;
  return (
    <span className="cost-plugin-chip" title={when ? `Plugin installé — première ouverture le ${when}` : 'Plugin installé'}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 22v-5" />
        <path d="M9 8V2" />
        <path d="M15 8V2" />
        <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8z" />
      </svg>
      {label}
    </span>
  );
}

/**
 * FunnelBlock — mini-funnel vertical : une barre par étape, largeur
 * proportionnelle à la 1re étape, compte + taux de passage vs l'étape
 * précédente à droite.
 */
function FunnelBlock({ title, color, steps }) {
  const base = Math.max(1, steps[0]?.count || 0);
  return (
    <div className="cost-funnel">
      <div className="cost-funnel-title" style={{ color }}>{title}</div>
      {steps.map((s, i) => {
        const prev = i === 0 ? null : steps[i - 1].count;
        const rate = prev == null ? null : prev > 0 ? Math.round((s.count / prev) * 100) : 0;
        const width = Math.max(2, (s.count / base) * 100);
        return (
          <div className="cost-funnel-row" key={s.label}>
            <div className="cost-funnel-label">{s.label}</div>
            <div className="cost-funnel-track">
              <div
                className="cost-funnel-bar"
                style={{
                  width: `${width}%`,
                  background: `${color}${i === 0 ? '55' : '33'}`,
                  borderColor: color,
                }}
              />
              <span className="cost-funnel-count">{s.count}</span>
            </div>
            <div className="cost-funnel-rate">
              {rate == null ? '—' : `${rate} %`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// pctOf — "12 %" (arrondi) ou "—" si total nul.
function pctOf(n, total) {
  if (!total) return '—';
  return `${Math.round((n / total) * 100)} %`;
}

function KpiCard({ label, value, sub, tone }) {
  return (
    <div className={`cost-kpi cost-kpi-${tone || 'amber'}`}>
      <div className="cost-kpi-label">{label}</div>
      <div className="cost-kpi-value">{value}</div>
      <div className="cost-kpi-sub">{sub}</div>
    </div>
  );
}

function UserDetail({ data }) {
  if (!data) return null;
  if (data.loading) return <div className="cost-detail-state">Chargement du détail…</div>;
  if (data.err) return <div className="cost-detail-state cost-detail-err">Erreur : {data.err}</div>;
  if (!data.items || data.items.length === 0) {
    return <div className="cost-detail-state">Aucun titre uploadé pour ce user.</div>;
  }
  // Group par track
  const byTrack = new Map();
  for (const it of data.items) {
    const k = it.track_id;
    if (!byTrack.has(k)) byTrack.set(k, { title: it.track_title, created_at: it.track_created_at, versions: [] });
    if (it.version_id) byTrack.get(k).versions.push(it);
  }
  return (
    <div className="cost-detail-wrap">
      {Array.from(byTrack.entries()).map(([trackId, t]) => (
        <div className="cost-detail-track" key={trackId}>
          <div className="cost-detail-track-head">
            <span className="cost-detail-track-title">{t.title || 'Sans titre'}</span>
            <span className="cost-detail-track-meta">créé le {fmtDate(t.created_at, true)} · {t.versions.length} version{t.versions.length > 1 ? 's' : ''}</span>
          </div>
          {t.versions.length > 0 && (
            <table className="cost-detail-versions">
              <thead>
                <tr>
                  <th>Version</th>
                  <th style={{textAlign: 'center'}}>Date</th>
                  <th style={{textAlign: 'right'}}>Durée</th>
                  <th style={{textAlign: 'right'}}>Coût</th>
                </tr>
              </thead>
              <tbody>
                {t.versions.map((v) => (
                  <tr key={v.version_id}>
                    <td>{v.version_name || '—'}</td>
                    <td style={{textAlign: 'center'}} className="cost-muted">{fmtDate(v.version_created_at, true)}</td>
                    <td style={{textAlign: 'right'}} className="cost-muted">{fmtDuration(v.audio_duration_sec)}</td>
                    <td style={{textAlign: 'right'}} className="cost-total">{v.cost_eur != null ? fmtEur(v.cost_eur) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * FeedbackTable — table des retours testeurs (les plus récents en haut).
 *
 * Une ligne par retour, cliquable pour expand inline et révéler les
 * verbatims complets. La ligne "header" résume : date, email tronqué,
 * NPS chip coloré, et un aperçu du verbatim le plus parlant
 * (oneliner > priority > friction > surprise > paywill).
 */
function FeedbackTable({ rows, userStats, expandedId, onToggleExpand }) {
  // Map user_id → email pour pouvoir afficher d'où vient chaque retour.
  const emailByUid = new Map((userStats || []).map((u) => [u.user_id, u.email]));
  // On affiche au maximum 50 retours dans le tableau (les plus récents).
  // Au-delà, on suppose que David exportera depuis Supabase Studio.
  const visible = (rows || []).slice(0, 50);

  if (visible.length === 0) return null;

  const verbatimPreview = (r) => {
    const candidates = ['oneliner', 'priority', 'friction', 'surprise', 'paywill'];
    for (const k of candidates) {
      const v = (r[k] || '').toString().trim();
      if (v) return v;
    }
    return '';
  };

  return (
    <div className="cost-table-wrap" style={{ marginTop: 18 }}>
      <table className="cost-table">
        <thead>
          <tr>
            <th style={{ width: 110 }}>Date</th>
            <th style={{ width: 70, textAlign: 'center' }}>NPS</th>
            <th>Email</th>
            <th>Aperçu verbatim</th>
            <th style={{ width: 60 }} aria-label=""></th>
          </tr>
        </thead>
        <tbody>
          {visible.map((r) => {
            const isOpen = expandedId === r.id;
            const email = emailByUid.get(r.user_id) || (r.user_id ? r.user_id.slice(0, 8) + '…' : 'anonyme');
            const preview = verbatimPreview(r);
            return (
              <FbRows
                key={r.id}
                row={r}
                email={email}
                preview={preview}
                isOpen={isOpen}
                onToggle={() => onToggleExpand(r.id)}
              />
            );
          })}
        </tbody>
      </table>
      {rows.length > visible.length && (
        <div className="cost-fadr-note" style={{ marginTop: 8 }}>
          Les {visible.length} retours les plus récents sont affichés ici (sur {rows.length} au total).
          Pour explorer plus loin, ouvre la table <code>feedback</code> dans Supabase Studio.
        </div>
      )}
    </div>
  );
}

/**
 * FbRows — wrapper qui rend la ligne header + la ligne expand
 * éventuelle. Sorti pour ne pas embrouiller le map de FeedbackTable
 * avec un fragment React.
 */
function FbRows({ row, email, preview, isOpen, onToggle }) {
  const npsTone =
    row.nps == null ? 'muted'
    : row.nps >= 9 ? 'mint'
    : row.nps >= 7 ? 'amber'
    : 'red';
  return (
    <>
      <tr
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
        className={isOpen ? 'cost-row-active' : undefined}
      >
        <td className="cost-muted">{fmtDate(row.created_at, true)}</td>
        <td style={{ textAlign: 'center' }}>
          {row.nps == null ? (
            <span className="cost-anon">—</span>
          ) : (
            <span className={`fb-nps-chip fb-nps-${npsTone}`}>{row.nps}</span>
          )}
        </td>
        <td className="cost-email">{email}</td>
        <td className="cost-feedback-preview">
          {preview ? (
            <span title={preview}>{preview.length > 90 ? preview.slice(0, 90) + '…' : preview}</span>
          ) : (
            <span className="cost-anon">— pas de verbatim —</span>
          )}
        </td>
        <td style={{ textAlign: 'right' }} className="cost-muted">
          {isOpen ? '▾' : '▸'}
        </td>
      </tr>
      {isOpen && (
        <tr className="cost-row-detail">
          <td colSpan={5}>
            <FeedbackDetail row={row} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * FeedbackDetail — vue dépliée d'un feedback. Affiche les 5 verbatims
 * en clair (vides masqués) + métadonnées de contexte (route, locale,
 * version_id si présent).
 */
function FeedbackDetail({ row }) {
  const fields = [
    { key: 'oneliner', label: 'Versions en une phrase' },
    { key: 'surprise', label: 'Surprise positive' },
    { key: 'friction', label: 'Friction / incompréhension' },
    { key: 'paywill', label: 'Prix juste / volonté de payer' },
    { key: 'priority', label: 'À changer en priorité' },
  ];
  const filled = fields.filter((f) => (row[f.key] || '').toString().trim().length > 0);
  return (
    <div className="cost-feedback-detail">
      {filled.length === 0 && (
        <div className="cost-detail-state">— aucun verbatim sur cette soumission —</div>
      )}
      {filled.map((f) => (
        <div key={f.key} className="cost-feedback-item">
          <div className="cost-feedback-item-label">{f.label}</div>
          <div className="cost-feedback-item-value">{row[f.key]}</div>
        </div>
      ))}
      <div className="cost-feedback-meta">
        {row.locale && <span>Langue : <strong>{row.locale}</strong></span>}
        {row.route && <span>Route : <code>{row.route}</code></span>}
        {row.version_id && <span>Version : <code>{String(row.version_id).slice(0, 8)}…</code></span>}
        {row.app_version && <span>App : <code>{row.app_version}</code></span>}
      </div>
    </div>
  );
}

/**
 * DailyChart — bar chart coût quotidien. Harmonisé 2026-07-10 avec
 * AcquisitionChart : ticks X hebdomadaires datés + survol interactif
 * (barre allumée, ligne guide, tooltip HTML avec analyses + coût).
 */
function DailyChart({ series }) {
  const [hover, setHover] = useState(null);
  const maxCost = Math.max(0.01, ...series.map((d) => d.total));
  const W = 1000, H = 200, PAD = 24;
  const barW = (W - PAD * 2) / series.length - 2;
  const xBar = (i) => PAD + i * (barW + 2);
  const xCenter = (i) => xBar(i) + Math.max(barW, 2) / 2;

  // Ticks hebdomadaires depuis le dernier jour (même logique que la
  // courbe acquisition).
  const ticks = [];
  for (let i = series.length - 1; i >= 0; i -= 7) ticks.push(i);

  const hovered = hover != null ? series[hover] : null;
  const tipFlip = hover != null && xCenter(hover) / W > 0.68;

  return (
    <div className="cost-chart">
      <div className="cost-chart-hoverwrap" onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="cost-chart-svg">
          {[0.25, 0.5, 0.75, 1].map((p) => (
            <line key={p} x1={PAD} x2={W - PAD}
              y1={H - PAD - (H - PAD * 2) * p}
              y2={H - PAD - (H - PAD * 2) * p}
              stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
          ))}
          {/* Ticks X hebdomadaires : trait + date lisible */}
          {ticks.map((i) => (
            <g key={`tick-${i}`}>
              <line x1={xCenter(i)} x2={xCenter(i)} y1={H - PAD} y2={H - PAD + 4}
                stroke="rgba(138,138,144,0.5)" strokeWidth="1" />
              <text x={xCenter(i)} y={H - 6} fill="rgba(138,138,144,0.85)"
                fontSize="9.5" fontFamily="monospace"
                textAnchor={i === series.length - 1 ? 'end' : i === 0 ? 'start' : 'middle'}>
                {fmtAxisDay(series[i].day)}
              </text>
            </g>
          ))}
          {series.map((d, i) => {
            const h = ((d.total / maxCost) || 0) * (H - PAD * 2);
            const y = H - PAD - h;
            const lit = hover === i;
            return (
              <rect key={d.day} x={xBar(i)} y={y} width={Math.max(barW, 2)} height={Math.max(h, 1)} rx="2"
                fill={lit ? 'rgba(245,166,35,0.9)' : 'rgba(245,166,35,0.55)'}
                stroke="rgba(245,166,35,0.9)" strokeWidth={lit ? 1 : 0.5} />
            );
          })}
          {/* Ligne guide au survol */}
          {hover != null && (
            <line x1={xCenter(hover)} x2={xCenter(hover)} y1={PAD - 6} y2={H - PAD}
              stroke="rgba(255,255,255,0.22)" strokeWidth="1" strokeDasharray="3 3" />
          )}
          {/* Zones de capture du survol (pleine hauteur, couvre les barres nulles) */}
          {series.map((d, i) => (
            <rect key={`h-${d.day}`} x={xBar(i) - 1} y={PAD - 10}
              width={Math.max(barW, 2) + 2} height={H - PAD * 2 + 14} fill="transparent"
              onMouseEnter={() => setHover(i)} />
          ))}
        </svg>
        {hovered && (
          <div
            className={`cost-chart-tip ${tipFlip ? 'cost-chart-tip-flip' : ''}`}
            style={{ left: `${(xCenter(hover) / W) * 100}%` }}
          >
            <div className="cost-chart-tip-date">{fmtTipDay(hovered.day)}</div>
            <div className="cost-chart-tip-row">
              <span className="cost-legend-dot" style={{ background: '#F5A623' }} />
              <strong>{hovered.count}</strong>&nbsp;analyse{hovered.count > 1 ? 's' : ''}
            </div>
            <div className="cost-chart-tip-row">
              <span className="cost-legend-dot" style={{ background: 'transparent' }} />
              <strong>{fmtEur(hovered.total)}</strong>&nbsp;de coûts API
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * AcquisitionChart — courbe double : inscriptions (céruléen) et
 * installations plugin (mint) par jour.
 *
 * Ergonomie (retour David 2026-07-10) :
 *   - Axe X : un repère daté par semaine ("11 juin", "18 juin", …) avec
 *     petit trait de tick, au lieu de 3 labels MM-DD cryptiques.
 *   - Survol : ligne guide verticale, points qui s'allument (halo), et
 *     tooltip HTML (date complète + valeurs colorées). Le tooltip est en
 *     HTML et pas en SVG pour ne pas être déformé par le
 *     preserveAspectRatio="none".
 */
function AcquisitionChart({ series }) {
  const [hover, setHover] = useState(null); // index du jour survolé ou null
  const W = 1000, H = 240, PADX = 34, PADY = 26;
  const maxVal = Math.max(1, ...series.map((d) => Math.max(d.signups, d.installs)));
  // Échelle Y : paliers ENTIERS (comptes de personnes) + air au-dessus du pic.
  const yMax = maxVal <= 4 ? maxVal + 1 : Math.ceil(maxVal * 1.15);
  const x = (i) => PADX + (i * (W - PADX * 2)) / Math.max(1, series.length - 1);
  const y = (v) => H - PADY - (v / yMax) * (H - PADY * 2);
  const linePts = (key) => series.map((d, i) => `${x(i)},${y(d[key])}`).join(' ');
  const areaPts = (key) =>
    `${PADX},${y(0)} ${linePts(key)} ${x(series.length - 1)},${y(0)}`;
  const step = Math.max(1, Math.ceil(yMax / 4));
  const gridVals = [];
  for (let v = step; v <= yMax; v += step) gridVals.push(v);
  const colW = (W - PADX * 2) / Math.max(1, series.length - 1);

  // Ticks X hebdomadaires : on part du dernier jour et on remonte de 7 en 7
  // pour que "aujourd'hui" ait toujours son repère.
  const ticks = [];
  for (let i = series.length - 1; i >= 0; i -= 7) ticks.push(i);

  const hovered = hover != null ? series[hover] : null;
  // Tooltip : bascule à gauche du curseur sur le dernier tiers du graphe
  // pour ne pas sortir du cadre.
  const tipFlip = hover != null && x(hover) / W > 0.68;

  return (
    <div className="cost-chart" style={{ marginTop: 18 }}>
      <div className="cost-chart-hoverwrap" onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="cost-chart-svg" style={{ height: 240 }}>
          {/* Grille + labels Y (entiers) */}
          {gridVals.map((v) => (
            <g key={v}>
              <line x1={PADX} x2={W - PADX} y1={y(v)} y2={y(v)}
                stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
              <text x={PADX - 6} y={y(v) + 3} fill="rgba(138,138,144,0.7)"
                fontSize="9" fontFamily="monospace" textAnchor="end">{v}</text>
            </g>
          ))}
          {/* Ticks X hebdomadaires : trait + date lisible */}
          {ticks.map((i) => (
            <g key={`tick-${i}`}>
              <line x1={x(i)} x2={x(i)} y1={H - PADY} y2={H - PADY + 4}
                stroke="rgba(138,138,144,0.5)" strokeWidth="1" />
              <line x1={x(i)} x2={x(i)} y1={PADY} y2={H - PADY}
                stroke="rgba(255,255,255,0.03)" strokeWidth="1" />
              <text x={x(i)} y={H - 6} fill="rgba(138,138,144,0.85)"
                fontSize="9.5" fontFamily="monospace"
                textAnchor={i === series.length - 1 ? 'end' : i === 0 ? 'start' : 'middle'}>
                {fmtAxisDay(series[i].day)}
              </text>
            </g>
          ))}
          {/* Aires légères sous chaque ligne */}
          <polygon points={areaPts('signups')} fill="rgba(92,184,204,0.08)" />
          <polygon points={areaPts('installs')} fill="rgba(142,224,122,0.08)" />
          {/* Ligne guide verticale au survol */}
          {hover != null && (
            <line x1={x(hover)} x2={x(hover)} y1={PADY} y2={H - PADY}
              stroke="rgba(255,255,255,0.22)" strokeWidth="1" strokeDasharray="3 3" />
          )}
          {/* Lignes */}
          <polyline points={linePts('signups')} fill="none"
            stroke="#5cb8cc" strokeWidth="2" strokeLinejoin="round" />
          <polyline points={linePts('installs')} fill="none"
            stroke="#8ee07a" strokeWidth="2" strokeLinejoin="round" />
          {/* Marqueurs sur les jours à activité (lisibilité des pics) */}
          {series.map((d, i) => (
            <g key={`m-${d.day}`}>
              {d.signups > 0 && (
                <rect x={x(i) - 2.5} y={y(d.signups) - 2.5} width="5" height="5" rx="1.5" fill="#5cb8cc" />
              )}
              {d.installs > 0 && (
                <rect x={x(i) - 2.5} y={y(d.installs) - 2.5} width="5" height="5" rx="1.5" fill="#8ee07a" />
              )}
            </g>
          ))}
          {/* Points allumés au survol : halo + point élargi, y compris à 0 */}
          {hover != null && (
            <g>
              <rect x={x(hover) - 6} y={y(hovered.signups) - 6} width="12" height="12" rx="4"
                fill="rgba(92,184,204,0.25)" />
              <rect x={x(hover) - 3.5} y={y(hovered.signups) - 3.5} width="7" height="7" rx="2"
                fill="#5cb8cc" stroke="#0b0b0e" strokeWidth="1.5" />
              <rect x={x(hover) - 6} y={y(hovered.installs) - 6} width="12" height="12" rx="4"
                fill="rgba(142,224,122,0.25)" />
              <rect x={x(hover) - 3.5} y={y(hovered.installs) - 3.5} width="7" height="7" rx="2"
                fill="#8ee07a" stroke="#0b0b0e" strokeWidth="1.5" />
            </g>
          )}
          {/* Zones de capture du survol : un rect invisible par jour */}
          {series.map((d, i) => (
            <rect key={`h-${d.day}`} x={x(i) - colW / 2} y={PADY - 10}
              width={colW} height={H - PADY * 2 + 14} fill="transparent"
              onMouseEnter={() => setHover(i)} />
          ))}
        </svg>
        {/* Tooltip HTML (non déformé par le preserveAspectRatio) */}
        {hovered && (
          <div
            className={`cost-chart-tip ${tipFlip ? 'cost-chart-tip-flip' : ''}`}
            style={{ left: `${(x(hover) / W) * 100}%` }}
          >
            <div className="cost-chart-tip-date">{fmtTipDay(hovered.day)}</div>
            <div className="cost-chart-tip-row">
              <span className="cost-legend-dot" style={{ background: '#5cb8cc' }} />
              <strong>{hovered.signups}</strong>&nbsp;inscription{hovered.signups > 1 ? 's' : ''}
            </div>
            <div className="cost-chart-tip-row">
              <span className="cost-legend-dot" style={{ background: '#8ee07a' }} />
              <strong>{hovered.installs}</strong>&nbsp;installation{hovered.installs > 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>
      <div className="cost-chart-legend">
        <span className="cost-legend-item"><span className="cost-legend-dot" style={{ background: '#5cb8cc' }} /> Inscriptions</span>
        <span className="cost-legend-item"><span className="cost-legend-dot" style={{ background: '#8ee07a' }} /> Installations plugin</span>
      </div>
    </div>
  );
}

// fmtAxisDay — "2026-06-11" → "11 juin" (labels d'axe compacts et lisibles).
const AXIS_MONTHS_FR = ['janv', 'févr', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc'];
function fmtAxisDay(iso) {
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  return `${d} ${AXIS_MONTHS_FR[m - 1] || ''}`;
}

// fmtTipDay — date complète du tooltip : "jeudi 10 juillet".
function fmtTipDay(iso) {
  try {
    return new Date(iso + 'T12:00:00').toLocaleDateString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long',
    });
  } catch {
    return iso;
  }
}

/* ── Helpers stats ────────────────────────────────────── */
function computeStats(logs) {
  const costs = logs.map((l) => Number(l.total_eur || 0)).filter((x) => !Number.isNaN(x));
  if (costs.length === 0) return { count: 0, total: 0, avg: 0, median: 0, p95: 0, min: 0, max: 0 };
  const total = costs.reduce((a, b) => a + b, 0);
  const sorted = [...costs].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  const p95 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))];
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  return { count: costs.length, total, avg: total / costs.length, median, p95, min, max };
}

// computeAcquisitionSeries — série quotidienne { day, signups, installs }
// sur N jours. Inscriptions depuis signed_up_at (admin_get_user_stats),
// installations depuis first_seen_at (admin_get_plugin_installs).
// Emails internes (équipe) exclus des agrégats d'installations plugin —
// même liste que STATS_EXCLUDE_EMAILS côté versions-api / cockpit.
const TEAM_EMAILS = new Set(['berdugo.david@gmail.com', 'davidabakan@gmail.com']);

function computeAcquisitionSeries(userStats, installs, days) {
  const map = new Map();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    map.set(key, { day: key, signups: 0, installs: 0 });
  }
  for (const u of userStats || []) {
    const slot = map.get((u.signed_up_at || '').slice(0, 10));
    if (slot) slot.signups += 1;
  }
  for (const p of installs || []) {
    const slot = map.get((p.first_seen_at || '').slice(0, 10));
    if (slot) slot.installs += 1;
  }
  return Array.from(map.values());
}

// computeFunnel — segmente les users par porte d'entrée (première action).
//   site    : première action = analyse web (ou seule action connue)
//   plugin  : première action = ouverture plugin
//   dormant : aucune action après le signup
// Et pré-calcule les étapes des deux funnels + le croisement.
function computeFunnel(rows) {
  const site = [], plugin = [];
  let dormant = 0, both = 0;
  for (const r of rows || []) {
    const fa = r.first_analysis_at, fp = r.first_plugin_at;
    if (!fa && !fp) { dormant += 1; continue; }
    if (fa && fp) both += 1;
    if (fa && (!fp || fa <= fp)) site.push(r);
    else plugin.push(r);
  }
  return {
    total: (rows || []).length,
    site,
    plugin,
    dormant,
    both,
    siteToPlugin: site.filter((r) => r.first_plugin_at).length,
    sitePaid: site.filter((r) => r.first_paid_at).length,
    pluginToSite: plugin.filter((r) => r.first_analysis_at).length,
    pluginPaid: plugin.filter((r) => r.first_paid_at).length,
  };
}

// computeSources — agrégats provenance (migration 051).
//   landingStats   : lignes { day, source, visits, cta_clicks } de
//                    admin_get_landing_stats (déjà agrégées jour × source).
//   userAttribution: lignes { user_id, email, source, utm_campaign,
//                    fbclid, first_seen_at, signed_up_at } de
//                    admin_get_user_attribution.
//   funnelRows     : admin_get_funnel — pour savoir si un inscrit Meta a
//                    ensuite lancé une analyse (first_analysis_at).
// Distinction inscription vs réactivation : un compte est une VRAIE
// inscription venue de la source si son signup est postérieur à la
// première arrivée avec cette provenance (marge 6 h pour absorber les
// horloges/lenteurs). Un vieux compte qui clique la pub aujourd'hui a
// first_seen_at >> signed_up_at → réactivation.
function computeSources(landingStats, userAttribution, funnelRows) {
  const SLACK_MS = 6 * 3600 * 1000;
  const bySource = new Map();
  const slot = (source) => {
    const key = source || 'autre';
    if (!bySource.has(key)) bySource.set(key, { source: key, visits: 0, cta: 0, signups: 0 });
    return bySource.get(key);
  };

  let totalVisits = 0;
  for (const r of landingStats || []) {
    const s = slot(r.source);
    s.visits += Number(r.visits) || 0;
    s.cta += Number(r.cta_clicks) || 0;
    totalVisits += Number(r.visits) || 0;
  }

  const analysisByUid = new Map();
  for (const f of funnelRows || []) analysisByUid.set(f.user_id, f.first_analysis_at || null);

  const metaSignups = [];
  const metaReturning = [];
  for (const a of userAttribution || []) {
    const isNewSignup =
      !a.first_seen_at || !a.signed_up_at
        ? true // pas de dates comparables → bénéfice du doute
        : new Date(a.signed_up_at).getTime() >= new Date(a.first_seen_at).getTime() - SLACK_MS;
    if (isNewSignup) slot(a.source).signups += 1;
    if (a.source === 'meta') {
      if (isNewSignup) metaSignups.push(a);
      else metaReturning.push(a);
    }
  }

  const meta = bySource.get('meta') || { source: 'meta', visits: 0, cta: 0, signups: 0 };
  const analyzed = metaSignups.filter((u) => analysisByUid.get(u.user_id)).length;

  const rows = Array.from(bySource.values()).sort(
    (x, y) => y.visits - x.visits || y.signups - x.signups
  );

  return {
    totalVisits,
    rows,
    meta: {
      visits: meta.visits,
      cta: meta.cta,
      signups: metaSignups,
      returning: metaReturning,
      analyzed,
    },
  };
}

// computePluginTotals — total d'utilisateurs plugin + répartition
// Mac / Windows / inconnu (platform déduite du dernier téléchargement
// loggé, cf. migration 045 — NULL si aucun download loggé).
function computePluginTotals(installs) {
  const t = { total: 0, mac: 0, windows: 0, unknown: 0 };
  for (const p of installs || []) {
    t.total += 1;
    if (p.platform === 'mac') t.mac += 1;
    else if (p.platform === 'windows') t.windows += 1;
    else t.unknown += 1;
  }
  return t;
}

function computeDailySeries(logs, days) {
  const map = new Map();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    map.set(key, { day: key, count: 0, total: 0 });
  }
  for (const l of logs) {
    const key = (l.created_at || '').slice(0, 10);
    const slot = map.get(key);
    if (slot) {
      slot.count += 1;
      slot.total += Number(l.total_eur || 0);
    }
  }
  return Array.from(map.values());
}

function computeTopUsers(logs, limit, userStats = []) {
  // Map user_id → email pour pouvoir afficher les emails dans le top
  const emailByUid = new Map(userStats.map((u) => [u.user_id, u.email]));
  const map = new Map();
  for (const l of logs) {
    const k = l.user_id || '__anon__';
    const cur = map.get(k) || { userId: l.user_id || null, count: 0, total: 0 };
    cur.count += 1;
    cur.total += Number(l.total_eur || 0);
    map.set(k, cur);
  }
  return Array.from(map.values())
    .map((u) => ({ ...u, email: emailByUid.get(u.userId) || null }))
    .sort((a, b) => b.total - a.total)
    .slice(0, limit);
}

// computeStripeStats — agrège les recettes sur N jours :
// brut TTC encaissé, frais Stripe (= brut - net), recettes nettes.
// La col `net_eur` est calculée par le webhook backend avec une approximation
// 1,5% + 0,25 € (cartes EEE). Pour les cartes UK (2,5% + 0,25 €) le chiffre
// peut être un peu surévalué. Pour la précision réelle, on récupèrera les
// `balance_transaction.fee` Stripe quand on en aura besoin.
function computeStripeStats(revenue, days = 30) {
  const cutoff = Date.now() - days * 86400 * 1000;
  let brut_30d = 0;
  let net_30d = 0;
  let count_30d = 0;
  for (const r of revenue || []) {
    const t = new Date(r.created_at).getTime();
    if (Number.isFinite(t) && t >= cutoff) {
      brut_30d += Number(r.amount_eur || 0);
      net_30d += Number(r.net_eur != null ? r.net_eur : r.amount_eur || 0);
      count_30d += 1;
    }
  }
  const fees_30d = brut_30d - net_30d;
  return { brut_30d, net_30d, fees_30d, count_30d };
}

// computeFadrMonth — agrège la conso Fadr du mois en cours :
// total minutes, € consommés (= minutes × tarif), nb d'analyses Fadr,
// durée moyenne, analyses restantes avant fin de franchise (basé sur
// la durée moyenne mesurée — fallback 4 min si aucune donnée).
function computeFadrMonth(logs) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const fadrLogs = logs.filter((l) => {
    if (!l.fadr_called) return false;
    if (!l.created_at) return false;
    return new Date(l.created_at) >= monthStart;
  });
  const minutesUsed = fadrLogs.reduce((acc, l) => acc + (Number(l.audio_duration_sec || 0) / 60), 0);
  const eurUsed = minutesUsed * FADR_EUR_PER_MIN;
  const count = fadrLogs.length;
  const avgDurationMin = count > 0 ? minutesUsed / count : 0;
  const minutesLeft = Math.max(0, FADR_PLAN_MINUTES - minutesUsed);
  // Estimation : combien d'analyses encore "gratuites" avant dépassement.
  // Prend la durée moyenne mesurée ; fallback 4 min si pas encore de data.
  const fallbackMin = 4;
  const usableAvg = avgDurationMin > 0 ? avgDurationMin : fallbackMin;
  const analysesLeft = count > 0 || fadrLogs.length === 0
    ? Math.max(0, Math.floor(minutesLeft / usableAvg))
    : null;
  const eurOver = Math.max(0, eurUsed - FADR_PLAN_EUR);
  const pctUsed = Math.min(1, minutesUsed / FADR_PLAN_MINUTES);
  return { minutesUsed, eurUsed, count, avgDurationMin, minutesLeft, analysesLeft, eurOver, pctUsed };
}

// computeChatStats — agrège les stats du chat sur la fenêtre fournie :
// nombre de tours, total €, moyenne par tour, et METRIQUES DE CACHING :
//   - cache_hit_rate : tokens lus depuis le cache / (lus + standard input)
//     → mesure l'efficacité du prompt caching (proche de 1 = très efficace).
//   - savings_eur    : économie estimée vs scénario "tout au tarif input plein"
//     → permet de chiffrer le gain réel du caching.
//
// Sur le tarif Sonnet 4.6 (2,76 €/M input) :
//   sans cache : (in + cache_w + cache_r) × 1.00
//   avec cache : in × 1.00 + cache_w × 1.25 + cache_r × 0.10
//   savings    = (cache_r × 0.90 - cache_w × 0.25) × tarif
function computeChatStats(chatLogs) {
  if (!chatLogs || chatLogs.length === 0) {
    return {
      count: 0, total: 0, avg: 0,
      total_in: 0, total_cache_w: 0, total_cache_r: 0, total_out: 0,
      cache_hit_rate: 0, savings_eur: 0,
      rag_hits: 0, rag_rate: 0,
    };
  }
  let total = 0;
  let total_in = 0;
  let total_cache_w = 0;
  let total_cache_r = 0;
  let total_out = 0;
  let rag_hits = 0;
  for (const l of chatLogs) {
    total += Number(l.total_eur || 0);
    total_in += Number(l.claude_in_tokens || 0);
    total_cache_w += Number(l.claude_cache_creation_tokens || 0);
    total_cache_r += Number(l.claude_cache_read_tokens || 0);
    total_out += Number(l.claude_out_tokens || 0);
    if (l.rag_used) rag_hits += 1;
  }
  const total_input_eq = total_in + total_cache_w + total_cache_r;
  const cache_hit_rate = total_input_eq > 0 ? (total_cache_r / total_input_eq) : 0;
  // Economie : par token caché lu, on paie 0.10 au lieu de 1.00 → 0.90 sauvé.
  // Mais l'écriture en cache coûte 1.25 au lieu de 1.00 → 0.25 surcoût (one-shot).
  // Tarif input Sonnet 4.6 = 2,76 €/M.
  const CLAUDE_IN_RATE = 2.76 / 1_000_000;
  const savings_eur = (total_cache_r * 0.90 - total_cache_w * 0.25) * CLAUDE_IN_RATE;
  return {
    count: chatLogs.length,
    total,
    avg: total / chatLogs.length,
    total_in, total_cache_w, total_cache_r, total_out,
    cache_hit_rate,
    savings_eur,
    rag_hits,
    rag_rate: rag_hits / chatLogs.length,
  };
}

// computeFeedbackStats — agrège les retours testeurs sur la fenêtre
// fournie (windowDays = nombre de jours, ou null pour tout l'historique).
//
// Calcule :
//   - count           : nombre total de soumissions
//   - npsCount        : nombre qui ont répondu à la question NPS (0..10)
//   - npsAvg          : moyenne arithmétique du NPS sur npsCount (sur 10)
//   - promoters       : nombre de NPS 9-10
//   - passives        : nombre de NPS 7-8
//   - detractors      : nombre de NPS 0-6
//   - npsScore        : score NPS classique = %promoters - %detractors
//                       (échelle -100..+100, viser > 0 puis > 30 puis > 50)
//   - verbatimRate    : % de soumissions ayant au moins UN verbatim non vide
function computeFeedbackStats(rows, windowDays = 30) {
  if (!rows || rows.length === 0) {
    return {
      count: 0, npsCount: 0, npsAvg: 0,
      promoters: 0, passives: 0, detractors: 0,
      npsScore: 0, verbatimRate: 0,
    };
  }
  let scoped = rows;
  if (windowDays != null) {
    const since = Date.now() - windowDays * 24 * 60 * 60 * 1000;
    scoped = rows.filter((r) => r.created_at && new Date(r.created_at).getTime() >= since);
  }
  if (scoped.length === 0) {
    return {
      count: 0, npsCount: 0, npsAvg: 0,
      promoters: 0, passives: 0, detractors: 0,
      npsScore: 0, verbatimRate: 0,
    };
  }
  let npsSum = 0, npsCount = 0;
  let promoters = 0, passives = 0, detractors = 0;
  let withVerbatim = 0;
  for (const r of scoped) {
    const n = (r.nps !== null && r.nps !== undefined) ? Number(r.nps) : null;
    if (n !== null && Number.isFinite(n) && n >= 0 && n <= 10) {
      npsSum += n;
      npsCount += 1;
      if (n >= 9) promoters += 1;
      else if (n >= 7) passives += 1;
      else detractors += 1;
    }
    const hasVerbatim = ['surprise', 'friction', 'paywill', 'oneliner', 'priority']
      .some((k) => (r[k] || '').toString().trim().length > 0);
    if (hasVerbatim) withVerbatim += 1;
  }
  const npsAvg = npsCount > 0 ? (npsSum / npsCount) : 0;
  const npsScore = npsCount > 0
    ? Math.round(((promoters / npsCount) - (detractors / npsCount)) * 100)
    : 0;
  return {
    count: scoped.length,
    npsCount,
    npsAvg,
    promoters,
    passives,
    detractors,
    npsScore,
    verbatimRate: withVerbatim / scoped.length,
  };
}

/* ── Helpers format ───────────────────────────────────── */
function fmtEur(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  if (Math.abs(v) < 0.01 && v !== 0) return v > 0 ? '< 0,01 €' : '> -0,01 €';
  return v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function fmtDuration(sec) {
  if (!sec) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
function fmtDate(iso, dateOnly = false) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (dateOnly) return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
    return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}
function pctMarge(coutMoyen, prixUnit) {
  if (!coutMoyen || coutMoyen <= 0) return '—';
  if (coutMoyen >= prixUnit) return '< 0 % (PERTE)';
  const m = ((prixUnit - coutMoyen) / prixUnit) * 100;
  return Math.round(m) + ' %';
}

/* ── Styles ────────────────────────────────────────────── */
function AdminStyles() {
  return (
    <style>{`
      .cost-screen {
        position: relative; z-index: 1;
        min-height: 100vh; min-height: 100dvh;
        background: transparent;
        color: var(--text, ${T.text});
        font-family: var(--body, ${T.body});
        overflow-x: hidden;
      }

      /* TOPBAR */
      .cost-topbar {
        position: relative; z-index: 2;
        max-width: 1480px; margin: 0 auto;
        padding: 22px 28px;
        display: flex; align-items: center; justify-content: space-between;
        gap: 24px;
      }
      .cost-topbar-brand {
        display: inline-flex; align-items: center; gap: 10px;
        background: transparent; border: 0; cursor: pointer;
        padding: 4px 6px; border-radius: 8px;
        transition: opacity .15s;
      }
      .cost-topbar-brand:hover { opacity: 0.82; }
      .cost-topbar-logo {
        height: 26px; width: auto;
        filter: drop-shadow(0 0 16px rgba(245,166,35,0.18));
      }
      .cost-topbar-wordmark {
        font-family: ${T.body}; font-weight: 700;
        font-size: 17px; letter-spacing: -0.4px;
        color: ${T.text}; line-height: 1;
      }
      .cost-topbar-wordmark .accent { color: ${T.amber}; font-style: normal; }

      .cost-topbar-nav { display: inline-flex; align-items: center; gap: 8px; }
      .cost-topbar-link, .cost-topbar-current {
        font-family: ${T.mono}; font-size: 11px; font-weight: 500;
        letter-spacing: 1.6px; text-transform: uppercase;
        padding: 9px 16px; border-radius: 999px;
        transition: all .15s;
      }
      .cost-topbar-link {
        background: transparent; color: ${T.textSoft};
        border: 1px solid transparent; cursor: pointer;
      }
      .cost-topbar-link:hover {
        color: ${T.text};
        background: rgba(255,255,255,0.04);
        border-color: rgba(255,255,255,0.10);
      }
      .cost-topbar-current {
        color: ${T.amber};
        background: rgba(245,166,35,0.06);
        border: 1px solid rgba(245,166,35,0.32);
      }

      /* GATE */
      .cost-gate {
        max-width: 480px; margin: 80px auto;
        text-align: center;
        padding: 48px 32px;
        background: ${T.s1};
        border: 1px solid ${T.border};
        border-radius: 16px;
      }
      .cost-gate-label {
        font-family: ${T.mono}; font-size: 12px; font-weight: 500;
        letter-spacing: 2px; text-transform: uppercase;
        color: ${T.amber};
      }
      .cost-gate-sub {
        margin-top: 12px;
        font-family: ${T.body}; font-size: 13.5px; font-weight: 300;
        color: ${T.textSoft};
      }

      /* HERO — calé sur la grammaire typo de la landing (.lp-slogan +
         .lp-tagline-trio + .lp-hero-sub) pour homogénéiser admin avec
         home / tarifs / dashboard. DM Sans 700 gros, em ambre SANS
         italique (règle visuelle Versions : italique réservé aux
         verdicts, jamais sur les mots ambres isolés). */
      .cost-hero {
        padding: clamp(48px, 7vw, 96px) 24px clamp(24px, 4vw, 48px);
        display: grid; place-items: center;
      }
      .cost-hero-inner {
        width: 100%; max-width: 880px;
        display: flex; flex-direction: column; align-items: center;
        gap: 18px; text-align: center;
        animation: fadeup .5s ease;
      }
      .cost-eyebrow {
        font-family: ${T.mono}; font-size: 11px; font-weight: 500;
        letter-spacing: 2.4px; color: ${T.amber}; text-transform: uppercase;
      }
      .cost-hero-title {
        font-family: ${T.body}; font-weight: 700;
        font-size: clamp(40px, 7vw, 80px);
        line-height: 0.98; letter-spacing: -2.4px;
        color: ${T.text}; margin: 0;
      }
      .cost-hero-title em {
        font-family: inherit; font-style: normal; font-weight: inherit;
        letter-spacing: inherit; color: ${T.amber};
      }
      .cost-hero-sub {
        font-family: ${T.body}; font-style: normal;
        font-size: 15px; font-weight: 300; line-height: 1.7;
        color: var(--soft, ${T.textSoft});
        max-width: 560px; margin: 0;
      }
      @media (max-width: 640px) {
        .cost-hero-title {
          font-size: clamp(34px, 10vw, 52px);
          letter-spacing: -1.2px;
        }
      }

      /* SECTIONS — eyebrow + titre H2 alignés sur la grammaire chips
         + .lp-tagline-trio. Em ambre SANS italique (cohérence). */
      .cost-section {
        padding: clamp(28px, 4vw, 48px) 24px;
        max-width: 1480px; margin: 0 auto;
      }
      .cost-section-eyebrow {
        font-family: ${T.mono}; font-size: 10.5px; font-weight: 500;
        letter-spacing: 2.2px; color: ${T.amber}; text-transform: uppercase;
        margin-bottom: 12px;
      }
      .cost-section-title {
        font-family: ${T.body}; font-weight: 500;
        font-size: clamp(20px, 2.4vw, 28px);
        line-height: 1.3; letter-spacing: -0.4px;
        color: ${T.textSoft}; margin: 0 0 24px;
      }
      .cost-section-title em {
        font-family: inherit; font-style: normal; font-weight: 600;
        color: ${T.amber};
      }

      /* KPIs */
      .cost-kpi-grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 16px;
      }
      .cost-kpi {
        position: relative; overflow: hidden;
        background: ${T.s1};
        border: 1px solid ${T.border};
        border-radius: 16px;
        padding: 22px 22px;
        display: flex; flex-direction: column; gap: 8px;
        transition: border-color .2s;
      }
      .cost-kpi::before {
        content: '';
        position: absolute;
        top: -40%; right: -25%;
        width: 220px; height: 220px;
        border-radius: 50%;
        filter: blur(48px);
        opacity: 0.5;
        pointer-events: none;
      }
      .cost-kpi > * { position: relative; z-index: 1; }
      .cost-kpi:hover { border-color: rgba(255,255,255,0.16); }

      .cost-kpi-amber::before { background: radial-gradient(circle, rgba(245,166,35,0.55), transparent 70%); }
      .cost-kpi-amber .cost-kpi-value { color: ${T.amber}; }
      .cost-kpi-cerulean::before { background: radial-gradient(circle, rgba(92,184,204,0.45), transparent 70%); }
      .cost-kpi-cerulean .cost-kpi-value { color: ${T.cerulean}; }
      .cost-kpi-violet::before { background: radial-gradient(circle, rgba(166,126,245,0.45), transparent 70%); }
      .cost-kpi-violet .cost-kpi-value { color: ${T.violet}; }
      .cost-kpi-mint::before { background: radial-gradient(circle, rgba(142,224,122,0.42), transparent 70%); }
      .cost-kpi-mint .cost-kpi-value { color: ${T.mint}; }
      .cost-kpi-red::before { background: radial-gradient(circle, rgba(255,93,93,0.42), transparent 70%); }
      .cost-kpi-red .cost-kpi-value { color: ${T.red}; }

      .cost-kpi-label {
        font-family: ${T.mono}; font-size: 10.5px; font-weight: 500;
        letter-spacing: 1.6px; text-transform: uppercase;
        color: ${T.muted};
      }
      .cost-kpi-value {
        /* Charte Versions : DM Sans 700 pour les chiffres clés, sans
           italique. Italique réservé aux verdicts (Cormorant).
           clamp(26, 2.5vw, 36) pour éviter overflow sur 13" : à 1280px
           viewport on tombe à 32px, à 1480+ on garde le 36px d origine. */
        font-family: ${T.body}; font-style: normal; font-weight: 700;
        font-size: clamp(26px, 2.5vw, 36px);
        line-height: 1; letter-spacing: -1px;
        color: ${T.text};
        /* Garde-fou si la valeur dépasse encore : ellipsis plutôt que
           débordement visible de la card. */
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .cost-kpi-sub {
        font-family: ${T.body}; font-size: 12px; font-weight: 300;
        color: ${T.muted2};
      }

      @media (max-width: 980px) { .cost-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
      @media (max-width: 520px) { .cost-kpi-grid { grid-template-columns: 1fr; } }

      /* FADR NOTE */
      .cost-fadr-note {
        margin-top: 16px;
        padding: 14px 18px;
        background: rgba(245,166,35,0.04);
        border: 1px solid rgba(245,166,35,0.16);
        border-radius: 10px;
        font-family: ${T.body}; font-size: 12.5px; font-weight: 300;
        color: ${T.textSoft}; line-height: 1.6;
      }
      .cost-fadr-note strong {
        font-weight: 600;
        color: ${T.red};
      }

      /* CHART */
      .cost-chart {
        background: ${T.s1};
        border: 1px solid ${T.border};
        border-radius: 16px;
        padding: 18px;
      }
      .cost-chart-svg { width: 100%; height: 200px; display: block; }
      .cost-chart-hoverwrap { position: relative; }
      /* Tooltip survol courbe acquisition — HTML au-dessus du SVG,
         suit le jour survolé, pointer-events none pour ne pas voler
         le survol aux zones de capture. */
      .cost-chart-tip {
        position: absolute;
        top: 12px;
        transform: translateX(12px);
        pointer-events: none;
        background: rgba(16,16,20,0.96);
        border: 1px solid ${T.border};
        border-radius: 10px;
        padding: 10px 13px;
        min-width: 150px;
        z-index: 5;
        box-shadow: 0 8px 24px rgba(0,0,0,0.45);
      }
      .cost-chart-tip-flip { transform: translateX(calc(-100% - 12px)); }
      .cost-chart-tip-date {
        font-family: ${T.mono}; font-size: 10px; font-weight: 600;
        letter-spacing: 1px; text-transform: uppercase;
        color: ${T.muted};
        margin-bottom: 7px;
      }
      .cost-chart-tip-row {
        display: flex; align-items: center; gap: 7px;
        font-family: ${T.body}; font-size: 12.5px; font-weight: 300;
        color: ${T.textSoft};
        line-height: 1.7;
      }
      .cost-chart-tip-row strong { font-weight: 700; color: ${T.text}; }
      .cost-chart-legend {
        display: flex; gap: 18px; justify-content: center;
        margin-top: 10px;
      }
      .cost-legend-item {
        display: inline-flex; align-items: center; gap: 7px;
        font-family: ${T.mono}; font-size: 10px; font-weight: 500;
        letter-spacing: 1.2px; text-transform: uppercase;
        color: ${T.muted2};
      }
      .cost-legend-dot {
        width: 9px; height: 9px; border-radius: 3px; display: inline-block;
      }

      /* FUNNELS PARCOURS D'ENTRÉE */
      .cost-funnel-grid {
        display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
        margin-top: 16px;
      }
      @media (max-width: 860px) { .cost-funnel-grid { grid-template-columns: 1fr; } }
      .cost-funnel {
        background: ${T.s1};
        border: 1px solid ${T.border};
        border-radius: 16px;
        padding: 18px 20px;
      }
      .cost-funnel-title {
        font-family: ${T.mono}; font-size: 10px; font-weight: 600;
        letter-spacing: 1.4px; text-transform: uppercase;
        margin-bottom: 14px;
      }
      .cost-funnel-row {
        display: grid;
        grid-template-columns: 190px 1fr 52px;
        align-items: center;
        gap: 12px;
        padding: 7px 0;
      }
      @media (max-width: 520px) { .cost-funnel-row { grid-template-columns: 130px 1fr 46px; } }
      .cost-funnel-label {
        font-family: ${T.body}; font-size: 12px; font-weight: 300;
        color: ${T.textSoft};
        line-height: 1.35;
      }
      .cost-funnel-track {
        position: relative;
        display: flex; align-items: center; gap: 9px;
        height: 24px;
      }
      .cost-funnel-bar {
        height: 100%;
        border-radius: 6px;
        border: 1px solid;
        min-width: 4px;
        transition: width .3s ease;
      }
      .cost-funnel-count {
        font-family: ${T.mono}; font-size: 12px; font-weight: 700;
        color: ${T.text};
      }
      .cost-funnel-rate {
        font-family: ${T.mono}; font-size: 11px; font-weight: 500;
        color: ${T.muted2};
        text-align: right;
      }

      /* OUTILS TABLE USERS (recherche + compteur) */
      .cost-table-tools {
        display: flex; align-items: center; gap: 12px;
        margin-bottom: 12px;
      }
      .cost-search {
        flex: 0 1 320px;
        background: ${T.s1};
        border: 1px solid ${T.border};
        border-radius: 10px;
        padding: 9px 14px;
        font-family: ${T.body}; font-size: 13px; font-weight: 300;
        color: ${T.text};
        outline: none;
        transition: border-color .15s;
      }
      .cost-search::placeholder { color: ${T.muted}; }
      .cost-search:focus { border-color: ${T.amberLine}; }
      .cost-search-reset {
        background: none;
        border: 1px solid ${T.border};
        border-radius: 8px;
        padding: 7px 12px;
        font-family: ${T.mono}; font-size: 10px; font-weight: 500;
        letter-spacing: 1px; text-transform: uppercase;
        color: ${T.muted2};
        cursor: pointer;
        transition: color .15s, border-color .15s;
      }
      .cost-search-reset:hover { color: ${T.text}; border-color: ${T.amberLine}; }
      .cost-search-count {
        margin-left: auto;
        font-family: ${T.mono}; font-size: 10px; font-weight: 500;
        letter-spacing: 1.2px; text-transform: uppercase;
        color: ${T.muted};
      }

      /* HEADERS TRIABLES */
      .cost-th-sort { cursor: pointer; user-select: none; transition: color .15s; }
      .cost-th-sort:hover { color: ${T.textSoft}; }
      .cost-th-sort-active { color: ${T.amber} !important; }
      .cost-th-arrow {
        display: inline-block;
        width: 12px;
        margin-left: 3px;
        font-size: 8px;
      }

      /* CHIP PLUGIN (table users) */
      .cost-plugin-chip {
        display: inline-flex; align-items: center; gap: 4px;
        padding: 3px 8px;
        border-radius: 6px;
        font-family: ${T.mono}; font-size: 9.5px; font-weight: 600;
        letter-spacing: 1px;
        color: ${T.mint};
        background: rgba(142,224,122,0.08);
        border: 1px solid rgba(142,224,122,0.3);
        line-height: 1;
        cursor: default;
      }

      /* TABLES */
      .cost-table-wrap {
        background: ${T.s1};
        border: 1px solid ${T.border};
        border-radius: 16px;
        overflow-x: auto;
      }
      .cost-table {
        width: 100%; border-collapse: collapse;
        font-family: ${T.body}; font-size: 13px;
      }
      .cost-table thead th {
        font-family: ${T.mono}; font-size: 10px; font-weight: 500;
        letter-spacing: 1.4px; text-transform: uppercase;
        color: ${T.muted};
        padding: 14px 14px;
        text-align: left;
        border-bottom: 1px solid ${T.border};
        background: rgba(255,255,255,0.015);
        white-space: nowrap;
      }
      .cost-table tbody tr {
        border-top: 1px solid ${T.border};
        transition: background .15s;
      }
      .cost-table tbody tr:hover { background: rgba(255,255,255,0.02); }
      .cost-table tbody td {
        padding: 12px 14px;
        color: ${T.text};
        white-space: nowrap;
      }
      .cost-row-clickable { cursor: pointer; }
      .cost-row-clickable:hover { background: rgba(245,166,35,0.04) !important; }
      .cost-row-open { background: rgba(245,166,35,0.06) !important; }
      .cost-row-detail td { padding: 0 !important; background: rgba(0,0,0,0.18); border-top: none !important; }
      /* Ligne feedback active (cliquée pour expand). Hover discret aussi. */
      .cost-row-active { background: rgba(92,184,204,0.05) !important; }
      .cost-table tbody tr:hover { background: rgba(255,255,255,0.025); }

      /* NPS chip — pill mono compact, couleur selon segment. */
      .fb-nps-chip {
        display: inline-flex; align-items: center; justify-content: center;
        min-width: 30px; height: 24px;
        padding: 0 8px; border-radius: 6px;
        font-family: ${T.mono}; font-size: 13px; font-weight: 600;
        line-height: 1;
        border: 1px solid currentColor;
      }
      .fb-nps-mint  { color: ${T.mint};  background: rgba(142,224,122,0.12); }
      .fb-nps-amber { color: ${T.amber}; background: rgba(245,166,35,0.10); }
      .fb-nps-red   { color: ${T.red};   background: rgba(255,93,93,0.10); }
      .fb-nps-muted { color: ${T.muted2}; background: rgba(255,255,255,0.04); border-color: rgba(255,255,255,0.10); }

      /* Aperçu verbatim — laisse passer le wrap normal mais n'étire pas
         la cellule. truncation sur l'aperçu textuel via JS (90 chars). */
      .cost-feedback-preview {
        white-space: normal !important;
        max-width: 520px;
        font-family: ${T.body}; font-size: 13px;
        color: ${T.textSoft}; line-height: 1.45;
      }

      /* Détail expand d'un feedback : grid de 5 verbatims max + meta. */
      .cost-feedback-detail {
        padding: 18px 20px 20px;
        display: flex; flex-direction: column; gap: 14px;
      }
      .cost-feedback-item {
        display: flex; flex-direction: column; gap: 4px;
      }
      .cost-feedback-item-label {
        font-family: ${T.mono}; font-size: 11px; letter-spacing: 1.5px;
        text-transform: uppercase; color: ${T.amber}; opacity: 0.85;
      }
      .cost-feedback-item-value {
        font-family: ${T.body}; font-size: 14px; color: ${T.text};
        line-height: 1.55; white-space: pre-wrap;
      }
      .cost-feedback-meta {
        display: flex; flex-wrap: wrap; gap: 14px;
        margin-top: 6px; padding-top: 12px;
        border-top: 1px solid rgba(255,255,255,0.06);
        font-family: ${T.mono}; font-size: 11px; color: ${T.muted};
        letter-spacing: 0.3px;
      }
      .cost-feedback-meta code {
        font-family: ${T.mono}; font-size: 11px;
        color: ${T.textSoft};
        background: rgba(255,255,255,0.04);
        padding: 1px 6px; border-radius: 4px;
      }
      .cost-feedback-meta strong { color: ${T.text}; font-weight: 600; }

      .cost-rank { font-family: ${T.mono}; font-weight: 500; color: ${T.amber}; }
      .cost-userid, .cost-email {
        font-family: ${T.mono}; font-size: 12px; color: ${T.textSoft};
      }
      .cost-email { color: ${T.text}; }
      .cost-anon { color: ${T.muted2}; font-style: normal; }
      /* Totaux + balance : DM Sans 600 (semi-bold) au lieu de Cormorant
         italic — charte Versions, italique exclusivement sur les verdicts. */
      .cost-total {
        font-family: ${T.body}; font-style: normal; font-weight: 600;
        font-size: 14px; color: ${T.amber};
      }
      .cost-balance-pos {
        font-family: ${T.body}; font-style: normal; font-weight: 600;
        font-size: 14px; color: ${T.mint};
      }
      .cost-balance-neg {
        font-family: ${T.body}; font-style: normal; font-weight: 600;
        font-size: 14px; color: ${T.red};
      }
      .cost-muted {
        color: ${T.muted};
        font-family: ${T.mono}; font-size: 12px;
      }
      .cost-empty {
        text-align: center;
        padding: 32px 16px !important;
        color: ${T.muted2};
        font-style: normal;
      }
      .cost-empty-card {
        padding: 32px 28px;
        background: ${T.s1};
        border: 1px dashed ${T.border};
        border-radius: 16px;
        text-align: center;
        color: ${T.textSoft};
        font-family: ${T.body}; font-size: 14px; font-weight: 300;
        line-height: 1.7;
      }
      .cost-empty-card code {
        font-family: ${T.mono}; font-size: 12px;
        color: ${T.amber};
        padding: 2px 6px; border-radius: 4px;
        background: rgba(245,166,35,0.08);
      }

      /* DETAIL EXPAND (sub-table dans une row) */
      .cost-detail-state {
        padding: 24px;
        font-family: ${T.body}; font-size: 13px; font-style: normal;
        color: ${T.muted};
        text-align: center;
      }
      .cost-detail-err { color: ${T.red}; }
      .cost-detail-wrap {
        padding: 18px 24px;
        display: flex; flex-direction: column; gap: 16px;
      }
      .cost-detail-track {
        background: rgba(255,255,255,0.02);
        border: 1px solid ${T.border};
        border-radius: 10px;
        overflow: hidden;
      }
      .cost-detail-track-head {
        padding: 12px 16px;
        display: flex; align-items: baseline; justify-content: space-between;
        gap: 16px;
        border-bottom: 1px solid ${T.border};
        background: rgba(245,166,35,0.04);
      }
      .cost-detail-track-title {
        /* DM Sans 600 — titres de tracks dans les expand admin, charte
           Versions (pas de Cormorant italic ici, réservé aux verdicts). */
        font-family: ${T.body}; font-style: normal; font-weight: 600;
        font-size: 16px; color: ${T.text};
      }
      .cost-detail-track-meta {
        font-family: ${T.mono}; font-size: 10.5px;
        letter-spacing: 1.2px; text-transform: uppercase;
        color: ${T.muted};
      }
      .cost-detail-versions {
        width: 100%; border-collapse: collapse;
        font-family: ${T.body}; font-size: 12.5px;
      }
      .cost-detail-versions th {
        font-family: ${T.mono}; font-size: 9.5px; font-weight: 500;
        letter-spacing: 1.2px; text-transform: uppercase;
        color: ${T.muted2};
        padding: 8px 12px;
        text-align: left;
        background: transparent;
        border-bottom: 1px solid rgba(255,255,255,0.04);
      }
      .cost-detail-versions td {
        padding: 9px 12px;
        border-top: 1px solid rgba(255,255,255,0.03);
        color: ${T.text};
      }

      @media (max-width: 720px) {
        .cost-table thead th, .cost-table tbody td {
          padding: 10px 10px; font-size: 12px;
        }
      }

      /* FOOTER */
      .cost-footer {
        padding: clamp(40px, 6vw, 72px) 24px;
        max-width: 1080px; margin: 0 auto;
        text-align: center;
        display: flex; flex-direction: column; align-items: center; gap: 14px;
      }
      .cost-footer-mark {
        font-family: 'Fredoka', ${T.body}; font-weight: 700;
        font-size: 22px; letter-spacing: 0.4px;
        color: ${T.text};
      }
      .cost-footer-mark .accent { color: ${T.amber}; }
      .cost-footer-line {
        font-family: ${T.body}; font-size: 13px; font-weight: 300;
        color: ${T.muted};
        max-width: 620px;
      }
      .cost-footer-line code {
        font-family: ${T.mono}; font-size: 12px;
        color: ${T.amber};
        padding: 2px 6px; border-radius: 4px;
        background: rgba(245,166,35,0.08);
      }

      @keyframes fadeup {
        from { opacity: 0; transform: translateY(12px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `}</style>
  );
}
